import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import {
  fetchSipCredentials,
  fetchCallerNumbers,
  isCallingConfigured,
  CALL_AUDIO_CONSTRAINTS,
  getPreferredAudioCodecs,
} from '@/lib/voip/callingService';
import {
  concreteDeviceFor,
  enumerateAudioDevices,
  isTrackUsable,
  readDevicePreferences,
  resolveDevice,
  swapCallMicrophone,
  trackDeviceId,
  writeDevicePreferences,
} from '@/lib/voip/audioDevices';
import {
  ensureMicrophoneAccess,
  readMicrophonePermission,
  type MicPermission,
} from '@/lib/voip/microphonePermission';
import {
  claimWarmMicrophone,
  releaseWarmMicrophone,
  warmMicrophone,
} from '@/lib/voip/warmMicrophone';
import { setCallsInFlight } from '@/lib/voip/callsInFlight';
import { isConferenceLegExpected, useConferenceStore } from './conference.store';
import { liveCallsApi } from '@/lib/calls/liveCalls';
import { callTransferApi } from '@/lib/calls/transfer.api';
import { createUuid } from '@notifi/uuid/browser';

export type CallState = 'idle' | 'ringing' | 'active' | 'ended';
export type RegistrationState = 'unregistered' | 'registering' | 'registered' | 'failed';
export type CallDirection = 'inbound' | 'outbound' | null;

export interface IncomingCallInfo {
  from: string;
  callerName: string | null;
  /** Matched CRM contact from operations' caller-ID lookup (via SSE), if any. */
  contactId: string | null;
  contactName: string | null;
}

// ── Caller-ID correlation cache ──────────────────────────────────────────────
// Telnyx rings this tab directly (giving us the raw number), while operations
// pushes a `call.incoming` SSE event carrying the matched contact. The two
// arrive independently and in either order, so we correlate them by phone
// number (last 10 digits) through this short-lived cache: whichever lands
// first stashes what it knows, and the second enriches the live toast.
interface CachedContact {
  contactId: string | null;
  contactName: string | null;
  at: number;
}
const contactByPhone = new Map<string, CachedContact>();
const CONTACT_TTL_MS = 60_000;

/** How to place a call, beyond who to ring. */
export interface StartCallOptions {
  /**
   * The line to present. Used when it is still one of the tenant's own lines;
   * otherwise the default caller ID is used. Calling a customer back on the
   * number they rang is what this exists for — they recognise it, and a return
   * call lands back on the same line.
   */
  fromNumber?: string | null;
}

/** Last 10 digits — a provider-agnostic match key across +1 / bare formats. */
function phoneKey(value: string | null | undefined): string | null {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : null;
}

function cacheContact(from: string, contactId: string | null, contactName: string | null): void {
  const key = phoneKey(from);
  if (!key) return;
  const now = Date.now();
  // Opportunistically prune stale entries so the map can't grow unbounded.
  for (const [k, v] of contactByPhone) {
    if (now - v.at > CONTACT_TTL_MS) contactByPhone.delete(k);
  }
  contactByPhone.set(key, { contactId, contactName, at: now });
}

function lookupContact(from: string): CachedContact | null {
  const key = phoneKey(from);
  if (!key) return null;
  const hit = contactByPhone.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > CONTACT_TTL_MS) {
    contactByPhone.delete(key);
    return null;
  }
  return hit;
}

// ── Loose Telnyx SDK types ──────────────────────────────────────────────────
// The real `@telnyx/webrtc` is dynamically imported (kept out of the initial
// bundle); tests inject a stub via `globalThis.__TelnyxRTCMock`. We only model
// the small surface we use, so neither path needs the full SDK types.
interface TelnyxCall {
  /** The SDK's LOCAL call id — a client-side UUID, unique to this browser leg. */
  id: string;
  /** The server-side ids (`v3:…`), exposed by the SDK through a getter — NOT the
   *  same as `id`, which is a browser-local UUID. The call logs key on
   *  `telnyxCallControlId`, so anything lining up with a call record must use it.
   *  Some builds also mirror it as a plain property; we read the getter first. */
  telnyxIDs?: { telnyxCallControlId?: string };
  telnyxCallControlId?: string;
  state?: string;
  direction?: string;
  options?: {
    remoteCallerNumber?: string;
    remoteCallerName?: string;
    /** Custom SIP headers from the INVITE, verbatim. The calling service states
     *  the CALLER's leg in `X-Caller-Leg` here — see {@link callerLegId}. */
    customHeaders?: Array<{ name?: string; value?: string }>;
    /** base64 JSON the calling service attached to the dial; carries the same
     *  caller leg as `originalCallId`. */
    clientState?: string;
    /** The SDK's own copy of the call's local media and mic id. A device swap
     *  rewrites both — mute/unmute and teardown read them. */
    localStream?: MediaStream | null;
    micId?: string;
    /** Paired with `micId`: the SDK falls back to it when the id has gone stale. */
    micLabel?: string;
  };
  remoteCallerNumber?: string;
  remoteCallerName?: string;
  /** Custom SIP headers on an inbound INVITE (the SDK surfaces them here on some
   *  builds, under `options.customHeaders` on others). */
  customHeaders?: Array<{ name?: string; value?: string }>;
  localStream?: MediaStream | null;
  remoteStream?: MediaStream | null;
  // The SDK's RTCPeerConnection wrapper. We close it ourselves at teardown so a
  // connection (and the audio-device handles it holds) can't survive the call.
  peer?: { instance?: RTCPeerConnection | null; close?: () => void | Promise<void> } | null;
  answer: () => void;
  /** Async since @telnyx/webrtc 2.26.1. Older builds returned void, so callers
   *  must tolerate both. */
  hangup: () => void | Promise<void>;
  /** @telnyx/webrtc 2.27+. The SDK's own account of how this call was set up,
   *  step by step, with offsets from the start. Absent on older builds — always
   *  call it optionally. See {@link establishmentSummary}. */
  getEstablishmentTimings?: () => {
    mode?: string;
    direction?: string;
    steps?: Array<{ name?: string; fromStart?: number }>;
  } | undefined;
  muteAudio?: () => void;
  unmuteAudio?: () => void;
  /** Re-acquire the microphone on a call that is already up: the SDK does the
   *  `getUserMedia` + `replaceTrack` and keeps its own stream in step. */
  setAudioInDevice?: (deviceId: string, muted?: boolean) => Promise<void>;
  hold?: () => void;
  unhold?: () => void;
  dtmf?: (digit: string) => void;
}
interface TelnyxClient {
  on: (event: string, cb: (notification: TelnyxNotification) => void) => void;
  connect: () => void;
  disconnect: () => void;
  newCall: (opts: Record<string, unknown>) => TelnyxCall;
}
type TelnyxClientCtor = new (opts: Record<string, unknown>) => TelnyxClient;
interface TelnyxNotification {
  type?: string;
  call?: TelnyxCall;
}

/**
 * One call the softphone is handling.
 *
 * The store used to keep a single `_activeCall`, so a second inbound ring
 * overwrote the first: the live call was orphaned, its remote audio detached,
 * and tearing either one down stopped the shared mic. Telnyx supports
 * concurrent calls natively (`BrowserSession.calls` is keyed by id, and every
 * call owns its own streams and hold state), so we model them the same way.
 */
export interface CallEntry {
  id: string;
  direction: Exclude<CallDirection, null>;
  status: 'ringing' | 'active' | 'held';
  /** Inbound only — who is calling, enriched by the caller-ID cache. */
  incoming: IncomingCallInfo | null;
  /** The other party's number, for the HUD. */
  peerNumber: string | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  /** When the call connected — the HUD timer reads this, so a second ring can
   *  no longer restart the first call's clock. */
  startedAt: number | null;
  /** Answered locally, but Telnyx has not confirmed the connection yet.
   *
   *  `answer()` is fire-and-forget — it sends the SIP 200 and returns, so the
   *  call is marked 'active' the instant the user taps, well before any
   *  connected callUpdate comes back. A caller who hangs up inside that window
   *  is reported asynchronously, and by then the call no longer looks like it is
   *  ringing, so cleanup that keys on 'ringing' alone skips it and the phantom
   *  survives. Cleared by the connected callUpdate. */
  answering?: boolean;
  /** Our leg is up and Telnyx has confirmed it — but the CALLER is not on it
   *  yet, because an inbound call still has to be bridged to them by the
   *  calling service. Deliberately separate from `answering`, which means
   *  "Telnyx has confirmed nothing" and is what per-call error cleanup keys on
   *  to drop a dead call: by this point the call is real and must survive a
   *  transient error. This one only holds the bar on "Connecting…" and keeps
   *  the clock stopped. See waitForCallerBridge. */
  awaitingBridge?: boolean;
  /** The initiator's own conference leg. It's a real audio call (its stream must
   *  stay audible) but the conference has its own UI, so the softphone HUD never
   *  renders a bar for it. */
  conference?: boolean;
  /** Outbound only: the random reference this browser stamped on the call's
   *  INVITE as X-Client-Ref. The calling service records which call it named,
   *  so presenting it later proves the call is this browser's own. */
  clientRef?: string;
  _call: TelnyxCall;
  _localStream: MediaStream | null;
}

/**
 * The id a call record is keyed by — the Telnyx call-control id (`v3:…`), read
 * from the SDK's `telnyxIDs` getter, falling back to a mirrored property and
 * finally the local id. The getter can throw on a half-torn-down call, so it's
 * guarded. Everything that hits a call-log endpoint (detail / transcribe /
 * summary) or links a request must go through this, never `entry.id`.
 */
export function callRecordId(entry: CallEntry): string {
  return telnyxCallControlId(entry) ?? entry.id;
}

/**
 * The Telnyx call-control id alone, or `undefined` when the SDK has only a
 * local leg id. Unlike {@link callRecordId} this never falls back to
 * `entry.id` — use it for anything PERSISTED or matched against a call log
 * (linking a request, resolving a log): a local SDK id matches no record, so
 * storing it would be worse than storing nothing.
 */
export function telnyxCallControlId(entry: CallEntry | null | undefined): string | undefined {
  // Every access guarded, not just the getter. `_call` is absent on a
  // half-torn-down entry, and the fallback below used to read straight through
  // it — the getter was wrapped and the plain property was not, which is a
  // crash on the exact call state the try/catch exists for.
  try {
    const fromGetter = entry?._call?.telnyxIDs?.telnyxCallControlId;
    if (fromGetter) return fromGetter;
    return entry?._call?.telnyxCallControlId ?? undefined;
  } catch {
    return undefined;
  }
}

/** The SIP header the calling service stamps the caller's leg into. */
export const CALLER_LEG_HEADER = 'X-Caller-Leg';

/**
 * The CALLER's call-control id — the only id the calling service can act on.
 *
 * {@link telnyxCallControlId} is this browser's OWN leg as the SIP endpoint sees
 * it. The calling service never indexed that id, so every lookup on it misses
 * and an action on the call (a transfer) comes back CALL_NOT_FOUND on a call
 * that is plainly still up. The caller's leg reaches us two ways instead, both
 * put there by the service that owns it:
 *
 *   1. `X-Caller-Leg`, a custom SIP header on the INVITE — stated outright.
 *      The SDK puts INVITE headers on the call itself on some builds and under
 *      `options` on others, so both are read, the same as the conference
 *      auto-join check.
 *   2. `client_state`, base64 JSON carrying `originalCallId` — the same id, for
 *      calls dialled before the header shipped.
 *
 * Returns undefined when neither is present, which is the signal to hide the
 * control rather than offer an action that cannot work.
 */
export function callerLegId(entry: CallEntry): string | undefined {
  for (const headers of [entry._call.customHeaders, entry._call.options?.customHeaders]) {
    if (!Array.isArray(headers)) continue;
    const match = headers.find(
      (h) => String(h?.name ?? '').toLowerCase() === CALLER_LEG_HEADER.toLowerCase(),
    );
    const value = match?.value?.trim();
    if (value) return value;
  }

  const raw = entry._call.options?.clientState;
  if (typeof raw === 'string' && raw) {
    try {
      const decoded = raw.trim().startsWith('{') ? raw : atob(raw);
      const parsed = JSON.parse(decoded) as { originalCallId?: unknown };
      if (typeof parsed.originalCallId === 'string' && parsed.originalCallId) {
        return parsed.originalCallId;
      }
    } catch {
      // Malformed client_state is not worth breaking the call UI over.
    }
  }

  return undefined;
}

/**
 * The one id a call is known by outside this browser: the caller's leg where the
 * call carries it, this browser's own leg where it does not (outbound).
 *
 * Everything keyed by a call has to agree on this. The call log is stored under
 * the caller leg, the job saves it as `sourceCallControlId`, and the AI intake
 * is written under whichever id pushes the transcript. When the push used the
 * agent leg and the job form read the caller leg, the intake landed on a row
 * nothing ever read — the transcript showed and the AI notes never did. So
 * every one of them goes through here rather than picking a leg for itself.
 *
 * Guarded because `_call` can be missing on a half-torn-down entry, and
 * `callerLegId` reads straight through it.
 */
export function callLogControlId(entry: CallEntry | null | undefined): string | undefined {
  if (!entry) return undefined;
  try {
    return callerLegId(entry) ?? telnyxCallControlId(entry);
  } catch {
    return telnyxCallControlId(entry);
  }
}

/**
 * Tell the live call monitor what only this softphone knows about its call — and,
 * when that is a hold, put music on the caller's line.
 *
 * Hold is a re-INVITE from the device; nothing on the server's call-control
 * path reports it, so managers watching live calls would never see "On hold"
 * unless the phone says so. And a browser-dialled outbound call carries no user
 * the server can see, so the agent's own client names itself on it (`{}`).
 *
 * WHY THE HOLD MUSIC IS SENT FROM HERE. The same re-INVITE that the monitor
 * cannot see is the reason a held caller hears NOTHING: the server never learns
 * about the hold, so nothing plays at them, and silence on a held line is
 * indistinguishable from a dropped call. A caller has to be told the hold
 * happened either way, and this is the one function every hold already goes
 * through — the explicit Hold button, parking a call to answer a second one, and
 * swapping between two calls. Putting it at those three sites instead would mean
 * three chances to add a fourth and forget.
 *
 * Both requests are fire-and-forget: the call never waits on either and never
 * fails because of either. A hold with no music is the silence callers get
 * today, so there is nothing here for the UI to report.
 *
 * Outbound calls whose id the SDK has not exposed use the same `active~<digits>`
 * alias the transcript stream uses.
 */
export function reportLiveAgentState(
  entry: CallEntry | null | undefined,
  report: { onHold?: boolean } = {},
): void {
  if (!entry || entry.conference) return;
  let id = callLogControlId(entry);
  if (!id && entry.direction === 'outbound') {
    const digits = (entry.peerNumber ?? '').replace(/\D/g, '');
    if (digits.length >= 4) id = `active~${digits}`;
  }
  if (!id) return;
  // The reference is what lets the server accept "this outbound call is mine":
  // a dialled-number alias alone could name a colleague's call.
  const body = entry.clientRef ? { ...report, clientRef: entry.clientRef } : report;
  void liveCallsApi.reportAgentState(id, body).catch(() => {
    // The monitor may be off on the server, or the call already ended.
  });

  // Only when this report IS about a hold. Every other caller of this function
  // is describing something else about the call, and must not touch the audio on
  // the customer's line.
  if (typeof report.onHold === 'boolean') {
    setHoldAudio(id, report.onHold, entry.clientRef);
  }
}

/**
 * The hold-audio requests for one call, in order, and only the ones that still
 * mean something.
 *
 * WHY ORDERING MATTERS HERE AND NOT FOR THE MONITOR REPORT. Two fire-and-forget
 * requests can arrive in either order. For the monitor that costs a stale badge
 * on a dashboard; here it costs a CALLER. An agent who taps Hold and resumes
 * straight away — a mis-click, or a swap and a swap back — sends "on" then "off",
 * and if "on" lands last the customer hears hold music over the conversation they
 * are already having, with nothing on the agent's screen to explain it and no
 * further event coming to fix it.
 *
 * So there is at most one request in flight per call, and when it finishes only
 * the LATEST intent is sent, if it still differs from what the server was last
 * told. A flurry of toggles collapses to one request carrying where the agent
 * actually ended up.
 *
 * Keyed by the id the server knows the call by, which is what the request is
 * addressed to; entries are dropped once the server agrees with us, so this never
 * grows beyond the calls currently being held.
 */
type HoldAudioState = {
  /** What the server has been told, as far as we know. */
  sent: boolean | null;
  /** What it should be told. */
  want: boolean;
  inFlight: boolean;
  clientRef?: string;
};
const holdAudioByCall = new Map<string, HoldAudioState>();

/**
 * A failed request is NOT proof that nothing happened: the calling service can
 * have started the music and the answer been lost on the way back — a proxy
 * timeout, a dropped connection. So a state the server has not confirmed is
 * retried rather than abandoned, because the state that matters is "stop", and
 * giving up on a stop leaves music over a live conversation with nothing left to
 * correct it.
 *
 * Bounded, and short: five attempts over about four seconds. A browser that
 * cannot reach the API for longer than that has almost certainly lost the call
 * as well, and an unbounded loop would be its own problem.
 */
const HOLD_AUDIO_RETRY_MS = 250;
const HOLD_AUDIO_MAX_ATTEMPTS = 5;

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Test seam: forget everything in flight. */
export function resetHoldAudioQueue(): void {
  holdAudioByCall.clear();
}

function setHoldAudio(id: string, onHold: boolean, clientRef?: string): void {
  const existing = holdAudioByCall.get(id);
  if (existing) {
    existing.want = onHold;
    if (clientRef) existing.clientRef = clientRef;
    // The request in flight will pick this up when it finishes. Sending now is
    // exactly the race this exists to remove.
    if (existing.inFlight) return;
  } else {
    holdAudioByCall.set(id, { sent: null, want: onHold, inFlight: false, clientRef });
  }
  void drainHoldAudio(id);
}

async function drainHoldAudio(id: string): Promise<void> {
  const state = holdAudioByCall.get(id);
  if (!state || state.inFlight) return;
  state.inFlight = true;
  try {
    let attempts = 0;
    while (state.want !== state.sent) {
      const target = state.want;
      try {
        await callTransferApi.holdAudio(id, target, state.clientRef);
        state.sent = target;
        // A fresh budget for whatever is asked next.
        attempts = 0;
      } catch {
        // `sent` is deliberately not updated: the server has not agreed to
        // anything. Waiting for "the next toggle" to retry was wrong — after a
        // resume there may BE no next toggle, and the request that failed may
        // still have reached the calling service and started the music. So the
        // unconfirmed state is re-sent, and `state.want` is re-read each time
        // around so a further toggle during the wait wins.
        attempts += 1;
        if (attempts >= HOLD_AUDIO_MAX_ATTEMPTS) return;
        await pause(HOLD_AUDIO_RETRY_MS * attempts);
      }
    }
  } finally {
    state.inFlight = false;
    // Settled: the server has been told where this call stands, and there is
    // nothing left to remember about it.
    if (state.want === state.sent) holdAudioByCall.delete(id);
  }
}

/** The header the calling service reads an outbound call's browser reference from. */
export const CLIENT_REF_HEADER = 'X-Client-Ref';

/** An unguessable per-call reference: it is the proof, so it must not be predictable. */
export function mintClientRef(): string {
  // createUuid is Web Crypto on both paths (randomUUID, else getRandomValues),
  // so the proof stays unguessable, and it is the generator the repo's UUID
  // contract requires.
  return createUuid();
}

/** Concurrent-call ceiling. Was 2, which silently dropped the THIRD inbound
 *  ring (no toast, no bar — the field-reported "caller 3 disconnects, callers
 *  1 and 2 unaffected"). The store is N-call by design (keyed map, per-call
 *  teardown, hold-current-and-accept, one bar per parked call), so the cap is
 *  purely a guardrail against runaway rings. 10 matches the mobile apps
 *  (iOS CallKit maximumCallGroups = 10). Past it, Telnyx keeps ringing the
 *  other registrations, so an over-cap call is still not lost. */
const MAX_CALLS = 10;

// The calling service tags the conference INITIATOR's own leg with this SIP
// header (see notifi-calling-service conferenceService). They started the
// conference, so their softphone auto-joins that leg silently instead of ringing
// — it's not an incoming call to them. Matched case-insensitively; if the SDK
// doesn't surface the header, the call simply rings as normal (safe fallback).
const CONF_AUTOJOIN_HEADER = 'x-notifi-conf-autojoin';

function isConferenceAutojoinLeg(call: TelnyxCall): boolean {
  const headers = call.customHeaders ?? call.options?.customHeaders ?? [];
  return (
    Array.isArray(headers) &&
    headers.some(
      (h) => typeof h?.name === 'string' && h.name.toLowerCase() === CONF_AUTOJOIN_HEADER,
    )
  );
}

interface VoipState {
  registration: RegistrationState;
  /** Every call in flight, keyed by Telnyx call id. */
  calls: Record<string, CallEntry>;
  /** The call the user is focused on — the one the HUD drives. */
  activeCallId: string | null;

  // ── Derived mirrors of the focused call ───────────────────────────────────
  // Recomputed on every mutation so existing consumers (dialer, HUD, ringtone)
  // keep working against the same names they always used.
  callState: CallState;
  direction: CallDirection;
  incoming: IncomingCallInfo | null;
  isMuted: boolean;
  isHeld: boolean;
  remoteStream: MediaStream | null;

  // Internal — not exposed via selector hooks.
  _client: TelnyxClient | null;
  /** The tenant's outbound caller-ID, resolved at register() and sent on every
   *  outbound call so Telnyx can place the PSTN leg. */
  _callerNumber: string | null;
  /** Every line this tenant may present, so a call back can pick one. */
  _callerNumbers: string[];
  /** The other party's number for the focused call, surfaced by the HUD. */
  _peerNumber: string | null;
  /** Selected audio devices (deviceId from enumerateDevices); null = system default. */
  _micId: string | null;
  _speakerId: string | null;
  /** The label each pinned device carried when it was chosen. Ids are
   *  regenerated when a device re-enumerates, so this is what lets us find the
   *  same physical headset again after it has been unplugged and plugged back
   *  in — see `resolveDevice`. */
  _micLabel: string | null;
  _speakerLabel: string | null;
  /** The output id the `<audio>` sinks should actually be on: `_speakerId`
   *  re-resolved against the devices that exist right now. */
  _activeSinkId: string | null;
  /** Bumped every time call audio has been re-routed. Output lives on `<audio>`
   *  elements the store can't touch, and a device coming back doesn't change
   *  `_speakerId`, so this is what tells them to re-apply `setSinkId`. */
  _audioEpoch: number;
  /** Another tab of this browser is on a call. Kept in step by
   *  `useCallPresence`; read by `ringModeOf`. */
  _peerBusy: boolean;
  /** Whether the call-waiting tone sounds. Persisted per browser. */
  _callWaitingTone: boolean;
  /** Whether this browser will give the softphone a microphone. Settled when
   *  the phone registers, so the question is asked with nobody on the line. */
  _micPermission: MicPermission;

  register: () => Promise<void>;
  unregister: () => void;
  /**
   * Apply operations' `call.incoming` SSE event: enrich the live incoming toast
   * with the matched CRM contact, correlated to the Telnyx ring by phone.
   */
  applyCallIncoming: (payload: {
    from?: string | null;
    contactId?: string | null;
    contactName?: string | null;
  }) => void;
  /** The calling service reporting that a caller's leg is now bridged to their
   *  agent — the moment the customer can hear them. Carries the CALLER's call
   *  control id and, in `agentLeg`, the leg the bridge webhook named. See
   *  {@link bridgedCallerLegs}. */
  applyCallBridged: (payload: {
    callControlId?: string | null;
    agentLeg?: string | null;
  }) => void;
  startCall: (destinationNumber: string, options?: StartCallOptions) => void;
  /**
   * Dial this tab into an internal call's room. The leg is a real audio call,
   * but it is flagged as a conference leg so the softphone bar, the incoming
   * toast and the ringtone all leave it to the conference UI. Returns why it
   * could not be placed, or null once the leg is on its way.
   */
  joinConferenceRoom: (dialTarget: string, callerNumber?: string | null) => string | null;
  /** Hang up this tab's conference legs, e.g. once the room has ended. */
  hangupConferenceLegs: () => void;
  /** Answer a ringing call (defaults to the one that is ringing). Any call
   *  already connected is put on hold rather than dropped. */
  answerCall: (callId?: string) => void;
  declineCall: (callId?: string) => void;
  /** End one call — defaults to the focused one. Other calls are untouched. */
  hangup: (callId?: string) => void;
  /** Put the focused call on hold and bring `callId` back. */
  swapTo: (callId: string) => void;
  muteToggle: (callId?: string) => void;
  holdToggle: (callId?: string) => void;
  sendDtmf: (digit: string, callId?: string) => void;
  /** Override the outbound caller-ID (e.g. from the dialer's Caller-ID picker). */
  setCallerNumber: (n: string | null) => void;
  /** Turn the call-waiting tone on or off (persisted per browser). */
  setCallWaitingTone: (enabled: boolean) => void;
  /** Audio-device selection from the dialer's Audio Settings. The label is
   *  remembered alongside the id so the choice survives a re-enumeration. */
  setMicId: (id: string | null, label?: string | null) => void;
  setSpeakerId: (id: string | null, label?: string | null) => void;
  /** Re-resolve the audio devices and move every live call onto them, swapping
   *  the mic track underneath the call rather than dropping it. Driven by
   *  `useAudioDeviceRecovery`. */
  recoverAudioDevices: () => Promise<AudioRecoveryResult>;
  /** Re-read the microphone permission without prompting — for after the agent
   *  has been told it is blocked and has gone to the browser to fix it. */
  refreshMicPermission: () => Promise<MicPermission>;
}

/** What one recovery pass did, so the caller can tell the CSR where audio went. */
export interface AudioRecoveryResult {
  /** How many live calls had their microphone re-acquired. */
  swapped: number;
  /** The device the microphone ended up on. */
  micLabel: string | null;
  /** A live call we could not get a working microphone back for. */
  failed: boolean;
}

type SetFn = (
  partial: Partial<VoipState> | ((state: VoipState) => Partial<VoipState>),
  replace?: false,
  actionName?: string,
) => void;

/** Resolve the TelnyxRTC constructor — a test seam first, else the real SDK
 *  (dynamic-imported to keep ~150 KB out of the initial bundle). */
/**
 * Which Telnyx signalling edge to register against.
 *
 * The default host does NOT route you to the nearest one. Measured from an
 * agent in South Asia: `rtc.telnyx.com` 210ms, `south-asia.rtc.telnyx.com`
 * 94ms — and call setup spends three to four round trips on that socket
 * (the SDK's own Performance Metrics report `New Call`, `Answer Send` and
 * `SDP Send` at roughly one RTT each). Picking the right edge is worth more
 * than anything left in this file.
 *
 * The SDK applies it by rewriting the host — `wss://<region>.rtc.telnyx.com` —
 * so a typo does not fail loudly, it registers against a hostname that does not
 * resolve and the phone simply never comes up. Hence the allow-list: an
 * unrecognised value is ignored and the default host is used.
 *
 * It belongs to the ENVIRONMENT, not the build: the same bundle serves agents
 * on different continents. Wired through vite.config.ts's `define` list so
 * /config.json can set it per deployment — a VITE_ var that is NOT in that list
 * silently reads as undefined in production, which is how the last one shipped
 * dead. See vite.config.ts.
 */
const TELNYX_REGIONS = [
  'eu', 'us-central', 'us-east', 'us-west', 'ca-central', 'apac', 'south-asia',
] as const;

/**
 * The edge nearest this agent, from their own clock.
 *
 * `VITE_TELNYX_REGION` is one value for a whole deployment, and a deployment
 * serves agents in several countries — a US setting is wrong for the Canadian
 * office and vice versa. The browser already knows where it is, so ask it.
 *
 * IANA zone names carry the country, which is all this needs: `America/Toronto`
 * and `America/Chicago` are both "America/" but belong to different edges. The
 * table is deliberately small and falls through to the continent, because a
 * wrong-but-nearby edge is still far better than the default — which is the EU
 * one for everybody, including Austin.
 */
function regionFromTimeZone(): string | undefined {
  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return undefined; // no Intl, or a locked-down browser — use the default
  }
  if (!zone) return undefined;

  // Canada first: its zones live under America/ alongside the US ones.
  if (/^America\/(Toronto|Montreal|Halifax|Winnipeg|Edmonton|Vancouver|Regina|St_Johns|Moncton|Iqaluit|Whitehorse|Yellowknife|Dawson|Glace_Bay|Goose_Bay|Blanc-Sablon|Rainy_River|Thunder_Bay|Nipigon|Atikokan|Cambridge_Bay|Creston|Fort_Nelson|Inuvik|Resolute|Rankin_Inlet|Swift_Current)$/.test(zone)) {
    return 'ca-central';
  }
  if (/^America\/(Los_Angeles|Vancouver|Tijuana|Phoenix|Boise|Anchorage|Juneau|Sitka|Nome|Denver)$/.test(zone)) {
    return 'us-west';
  }
  if (/^America\/(New_York|Detroit|Toronto|Indiana|Kentucky|Nassau)/.test(zone)) return 'us-east';
  if (/^America\//.test(zone) || zone === 'US/Central') return 'us-central';
  if (/^(Europe|Africa|Atlantic)\//.test(zone)) return 'eu';
  if (/^Asia\/(Kolkata|Calcutta|Colombo|Karachi|Dhaka|Kathmandu|Thimphu)$/.test(zone)) {
    return 'south-asia';
  }
  if (/^(Asia|Australia|Pacific|Indian)\//.test(zone)) return 'apac';
  return undefined;
}

function telnyxRegion(): string | undefined {
  // THE AGENT'S OWN LOCATION COMES FIRST, and the configured value is only a
  // fallback for when the browser cannot say.
  //
  // It was the other way round, and that was a trap: one deployment serves
  // agents on different continents — the person running this is in India and
  // their customers are in the United States and Canada — so anything set once
  // for the whole environment is wrong for most of the people using it. An
  // override that wins would let a single well-meaning config entry pin every
  // agent worldwide to one edge, which is precisely the bug being fixed here:
  // the Telnyx default is a single edge for everyone, and it is in Europe.
  //
  // So there is no way to configure the wrong answer for someone whose browser
  // knows the right one.
  const detected = regionFromTimeZone();
  if (detected) return detected;

  const raw = (import.meta.env?.VITE_TELNYX_REGION as string | undefined)?.trim().toLowerCase();
  if (!raw) return undefined;
  if ((TELNYX_REGIONS as readonly string[]).includes(raw)) return raw;
  console.warn(`[voip] ignoring unknown VITE_TELNYX_REGION "${raw}"; using the default edge`);
  return undefined;
}

async function resolveTelnyxRTC(): Promise<TelnyxClientCtor> {
  const seam = (globalThis as { __TelnyxRTCMock?: TelnyxClientCtor }).__TelnyxRTCMock;
  if (seam) return seam;
  const mod = await import('@telnyx/webrtc');
  return mod.TelnyxRTC as unknown as TelnyxClientCtor;
}

function callerInfo(call: TelnyxCall): IncomingCallInfo {
  const from = call.options?.remoteCallerNumber ?? call.remoteCallerNumber ?? '';
  // If the SSE `call.incoming` already landed for this number, start the toast
  // with the matched contact instead of a bare number.
  const cached = lookupContact(from);
  return {
    from,
    callerName: call.options?.remoteCallerName ?? call.remoteCallerName ?? null,
    contactId: cached?.contactId ?? null,
    contactName: cached?.contactName ?? null,
  };
}

/**
 * Normalise an optional call-id argument.
 *
 * These actions are public and routinely handed straight to a React handler
 * (`onClick={declineCall}`), which calls them with a MouseEvent as the first
 * argument. TypeScript cannot catch that — a `() => void` prop accepts a
 * function with optional params, and React passes the event regardless — so the
 * id would be an event object, the lookup would miss, and the button would
 * silently do nothing. Coerce anything that isn't a string back to "unspecified".
 */
const asCallId = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/**
 * Why this tab can't dial into an internal call right now, or null if it can.
 * Checked before the room is created as well as when dialling, so a busy agent
 * never leaves a room behind that nobody will enter.
 */
export function conferenceJoinBlocker(
  state: Pick<VoipState, '_client' | 'callState' | 'calls'>,
): string | null {
  if (!state._client) return "Your phone line isn't connected yet. Try again in a moment.";
  // A second audio call would put the customer on hold, and an internal call
  // must never be able to do that on its own.
  if (Object.values(state.calls).some((c) => c.conference)) return "You're already on a team call.";
  if (state.callState !== 'idle') return 'Finish your current call first.';
  return null;
}

export const ringingCallsOf = (calls: Record<string, CallEntry>): CallEntry[] =>
  Object.values(calls).filter((c) => c.status === 'ringing');

/**
 * What the softphone should sound like right now.
 *
 * - `ring`    — an inbound call is ringing and nothing else is on the line.
 * - `waiting` — an inbound call is ringing while the user is already on the
 *               phone (a connected, held, or outbound call).
 * - `silent`  — nothing inbound is ringing.
 *
 * Why `waiting` exists: the full ringtone looping out of the speakers during a
 * live call goes straight into the mic, so the caller hears it echoed back and
 * the agent can no longer hear the caller over it. Desk phones solve this with a
 * short, quiet call-waiting beep; the HUD's ringing bar carries the rest.
 *
 * Reads the call map rather than the flat `callState`/`direction` mirrors: those
 * describe the FOCUSED call, so with one call already up they point at it and a
 * second caller would ring silently.
 */
export type RingMode = 'ring' | 'waiting' | 'silent';

/** Whether the call-waiting tone sounds at all. Persisted per browser: a CSR
 *  who turns it off means it, and a preference that reset on every reload would
 *  be no use to them. */
const CALL_WAITING_TONE_KEY = 'notifi.voip.callWaitingTone';

function readCallWaitingTone(): boolean {
  try {
    return localStorage.getItem(CALL_WAITING_TONE_KEY) !== 'off';
  } catch {
    // Private mode / storage blocked — the tone is on, which is the default.
    return true;
  }
}

/**
 * The mic and speaker the agent last chose, as store state.
 *
 * Read once when the store is created, the same moment the call-waiting tone
 * is. Without it the softphone came up on the system default after every
 * reload — on a docked laptop, the built-in speaker — and the headset had to be
 * picked again from the Audio Settings panel each time.
 *
 * `_activeSinkId` starts on the remembered id rather than null because the
 * `<audio>` sinks need an output the moment a call arrives, and the re-resolve
 * against the live device list only runs while a call is up. Picking a speaker
 * by hand sets it exactly the same way. A remembered id that no longer exists
 * costs an already-handled `setSinkId` rejection, and the first recovery pass
 * re-pins it by label.
 */
function rememberedAudioDevices() {
  const { mic, speaker } = readDevicePreferences();
  return {
    _micId: mic.id,
    _micLabel: mic.label,
    _speakerId: speaker.id,
    _speakerLabel: speaker.label,
    _activeSinkId: speaker.id,
  };
}

/** Write both choices back. They share one stored value, so saving either one
 *  has to carry the other — hence reading them off the state rather than
 *  taking the pair that happens to be at hand. */
function rememberAudioDevices(state: VoipState): void {
  writeDevicePreferences({
    mic: { id: state._micId, label: state._micLabel },
    speaker: { id: state._speakerId, label: state._speakerLabel },
  });
}

const isInboundRing = (c: CallEntry) => c.status === 'ringing' && c.direction === 'inbound';

/** Something other than an inbound ring is up — a connected call, a held one, or
 *  an outbound attempt. This is what makes a new inbound call "call waiting",
 *  and what this tab advertises to its siblings. */
export const hasCallInProgress = (calls: Record<string, CallEntry>): boolean =>
  Object.values(calls).some((c) => !isInboundRing(c));

/**
 * `peerBusy` is another tab of this browser being on the call. All tabs share
 * one SIP credential, so Telnyx rings every one of them, but each has its own
 * call map — without this the idle tab sees nothing in progress and blasts the
 * full ringtone over the conversation happening next door. See `callPresence`.
 */
export const ringModeOf = (calls: Record<string, CallEntry>, peerBusy = false): RingMode => {
  if (!Object.values(calls).some(isInboundRing)) return 'silent';
  return hasCallInProgress(calls) || peerBusy ? 'waiting' : 'ring';
};

/**
 * What should actually be sounding — {@link ringModeOf} with the CSR's own
 * preference applied. Turning the tone off silences call waiting only; a call
 * arriving when nothing else is up still rings.
 *
 * Everything that decides what to play goes through this one function. The
 * ringtone has two such decision points (the render and the autoplay unlock),
 * and reading the state two different ways is exactly how they drift.
 */
/**
 * Keep this tab's copy of the tone preference in step with the others.
 *
 * Telnyx rings every tab the user has open, and since the cross-tab presence
 * fix the idle one is exactly the tab that sounds the call-waiting cue. The
 * preference is read from storage once, when the store is created, so without
 * this a CSR could switch the tone off in the tab they are working in and go on
 * hearing it from the one next door — having asked for it off.
 *
 * `storage` fires only in the OTHER tabs, which is the propagation we want: the
 * tab that made the change has already applied it.
 */
export function subscribeToCallWaitingTone(): () => void {
  const onStorage = (event: StorageEvent) => {
    // A null key means the whole store was cleared, which counts.
    if (event.key !== null && event.key !== CALL_WAITING_TONE_KEY) return;
    useVoipStore.setState(
      { _callWaitingTone: readCallWaitingTone() },
      false,
      'voip/callWaitingToneSynced',
    );
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

export const audibleRingModeOf = (state: {
  calls: Record<string, CallEntry>;
  _peerBusy: boolean;
  _callWaitingTone: boolean;
}): RingMode => {
  const mode = ringModeOf(state.calls, state._peerBusy);
  return mode === 'waiting' && !state._callWaitingTone ? 'silent' : mode;
};

/** Calls that are up but not focused — shown as their own bar so a held caller
 *  is never invisible. */
export const backgroundCallsOf = (
  calls: Record<string, CallEntry>,
  activeCallId: string | null,
): CallEntry[] =>
  Object.values(calls).filter((c) => c.status !== 'ringing' && c.id !== activeCallId);

/**
 * Project the focused call onto the flat fields the UI has always read.
 *
 * The important part is what this does NOT do: a second inbound ring adds an
 * entry but never becomes the focus while a call is connected, so `callState`
 * stays 'active' and the HUD keeps showing (and timing) the call in progress.
 */
function derive(
  calls: Record<string, CallEntry>,
  activeCallId: string | null,
): Pick<
  VoipState,
  'callState' | 'direction' | 'incoming' | 'isMuted' | 'isHeld' | 'remoteStream' | '_peerNumber'
> {
  const focused = activeCallId ? (calls[activeCallId] ?? null) : null;
  const list = Object.values(calls);
  // With nothing connected, an outbound attempt outranks an inbound ring: it is
  // the one the user just started.
  const fallback =
    list.find((c) => c.status === 'ringing' && c.direction === 'outbound') ??
    list.find((c) => c.status === 'ringing' && c.direction === 'inbound') ??
    null;
  const subject = focused ?? fallback;

  return {
    callState: focused ? 'active' : subject ? 'ringing' : 'idle',
    direction: subject?.direction ?? null,
    // Only surface the incoming card when there is nothing connected — with a
    // call up, ringing calls are rendered as their own bar by the HUD instead.
    incoming: focused ? null : fallback?.direction === 'inbound' ? fallback.incoming : null,
    isMuted: subject?.isMuted ?? false,
    isHeld: focused?.status === 'held',
    remoteStream: focused?.remoteStream ?? null,
    _peerNumber: subject?.peerNumber ?? null,
  };
}

/** Commit a new call map plus everything derived from it. */
function commit(
  set: SetFn,
  calls: Record<string, CallEntry>,
  activeCallId: string | null,
  action: string,
): void {
  set({ calls, activeCallId, ...derive(calls, activeCallId) }, false, action);
}

/** Stop every track on a stream, swallowing errors from an already-ended one. */
function stopStreamTracks(stream: MediaStream | null | undefined): void {
  try {
    stream?.getTracks().forEach((t) => t.stop());
  } catch {
    // Track already stopped / stream gone — nothing to release.
  }
}

/** Every local track a set of calls is still sending, by identity.
 *
 *  Tracks, not streams: the SDK can attach the same mic `MediaStreamTrack` to
 *  two peer connections without the calls sharing a `MediaStream` object, so
 *  comparing streams alone would miss it. */
function localTracksInUseBy(entries: CallEntry[]): Set<MediaStreamTrack> {
  const tracks = new Set<MediaStreamTrack>();
  const add = (t: MediaStreamTrack | null | undefined) => {
    if (t) tracks.add(t);
  };
  for (const c of entries) {
    for (const stream of [c._localStream, c._call.localStream]) {
      try {
        stream?.getTracks().forEach(add);
      } catch {
        // Stream already gone — nothing it can still be using.
      }
    }
    try {
      c._call.peer?.instance?.getSenders?.().forEach((sender) => add(sender.track));
    } catch {
      // Connection already closed — same.
    }
  }
  return tracks;
}

/** Close the call's RTCPeerConnection and stop its senders/receivers. The SDK's
 *  hangup() should do this, but if it doesn't, an un-closed connection keeps its
 *  audio-device handles alive — so repeated call/hang-up cycles pile up captures
 *  until the browser (and macOS CoreAudio) chokes. Closing it ourselves is
 *  idempotent, so doing it belt-and-suspenders is safe.
 *
 *  `keep` spares tracks another live call is still sending. Without it this
 *  undoes the shared-capture guard in endCall: the sender loop would stop the
 *  very mic track the surviving call is transmitting, muting a call the user
 *  never touched. The connection is still closed either way — only the shared
 *  track survives, owned now by the call that is still using it. */
function releasePeer(call: TelnyxCall | null, keep: Set<MediaStreamTrack> = new Set()): void {
  const peer = call?.peer;
  const pc = peer?.instance ?? null;
  try {
    pc?.getSenders?.().forEach((s) => {
      if (s.track && !keep.has(s.track)) s.track.stop();
    });
    // Receivers carry the REMOTE audio of this call alone — always released.
    pc?.getReceivers?.().forEach((r) => r.track?.stop());
  } catch {
    // Connection already closed — ignore.
  }
  try {
    void peer?.close?.();
  } catch {
    // Ignore — may already be closed.
  }
  try {
    pc?.close?.();
  } catch {
    // Ignore — may already be closed.
  }
}

// Re-entrancy guard: ending a call invokes the SDK's hangup(), which may
// synchronously emit a terminal notification for the same call. The set
// collapses those repeats into one pass.
const tearingDown = new Set<string>();



/**
 * Which registration attempt is current.
 *
 * `register()` does async work — the SIP credentials and caller numbers — before
 * it can construct a client, and its "am I already registering?" guard is read
 * BEFORE that await. So an effect that re-runs while the fetch is in flight got
 * past the guard a second time, and the first attempt still built its client
 * when the fetch resolved: two TelnyxRTC instances, two SIP registrations for
 * one credential, neither aware of the other. Telnyx then forks every inbound
 * call to both.
 *
 * Visible in dev on every load, because StrictMode mounts, cleans up and mounts
 * again — two "SDK version" lines about 400ms apart, which is exactly how long
 * the credential fetch takes. But it is not only a dev artifact: the effect is
 * keyed on `[tenantId, userId]` and both resolve asynchronously on first load,
 * so a production session can re-run it mid-flight and land in the same place.
 *
 * Bumped by every `unregister()`. An attempt that finds the epoch moved on since
 * it started throws its client away instead of installing it.
 */
let registrationEpoch = 0;

/**
 * Bringing the phone back after a registration that failed.
 *
 * `useVoipRegistration` calls `register()` exactly once, when the dashboard
 * mounts, and never again. That is fine for the happy path and wrong for the
 * shape of the actual working day: an agent leaves this tab open for eight
 * hours, the laptop sleeps, the Wi-Fi moves, the first registration loses a
 * race with a slow network — and nothing ever asks for the phone back. The tab
 * keeps looking normal and silently stops being ringable, which is worse than
 * an error, because nobody finds out until a customer says nobody answered.
 *
 * A live session recovers on its own: the SDK reconnects with its own backoff,
 * and `maxReconnectAttempts: 0` stops it ever giving up (see register()). This
 * covers the other case — a registration that never came up, where there is no
 * session to reconnect and no SDK timer running.
 *
 * Backoff matches the SDK's: 1s doubling to a 30s ceiling, with jitter, so a
 * tab left open overnight retries about twice a minute forever rather than
 * hammering the credentials endpoint.
 */
let reviveTimer: ReturnType<typeof setTimeout> | null = null;
let reviveAttempt = 0;

/** Stop any pending revival. Called when the phone comes up, and when the
 *  agent deliberately takes it down — logging out must not resurrect it. */
function cancelRegistrationRevival(): void {
  if (reviveTimer !== null) clearTimeout(reviveTimer);
  reviveTimer = null;
  reviveAttempt = 0;
}

function scheduleRegistrationRevival(get: () => VoipState): void {
  if (reviveTimer !== null) return; // one in flight is enough
  const attempt = (reviveAttempt += 1);
  const base = Math.min(1000 * 2 ** (attempt - 1), 30_000);
  const delay = base + Math.floor(base * 0.25 * (2 * Math.random() - 1));
  // Captured now: anything that re-registers or unregisters in the meantime
  // moves the epoch, and this attempt is then answering a question nobody is
  // asking any more.
  const epoch = registrationEpoch;
  reviveTimer = setTimeout(() => {
    reviveTimer = null;
    if (epoch !== registrationEpoch) return;
    const { registration } = get();
    // Only a phone that is still down. 'registering' means something newer is
    // already trying, and anything else means it came back without us.
    if (registration !== 'failed') return;
    // `register()` returns early while a client object exists, so the dead one
    // has to go first. There are no calls to lose: a registration that never
    // came up cannot have any.
    get().unregister();
    void get().register();
  }, delay);
}

/**
 * When the agent pressed Answer, by call id.
 *
 * The spinner they watch starts HERE — the card goes up the instant they click.
 * The bridge wait is only armed later, once the SDK reports the call connected,
 * so timing from there measured the tail of the spinner and not the spinner:
 * a log line said 52ms for a wait the agent saw for 879ms. A measurement that
 * flatters itself is worse than none, because it gets believed.
 */
const answeredAt = new Map<string, number>();

/** Pending bridge waits, by call id — cancelled if the call ends first. Each
 *  holds both the audio poll and the ceiling that backstops it. */
const bridgeWaits = new Map<
  string,
  { interval: ReturnType<typeof setInterval>; ceiling: ReturnType<typeof setTimeout> }
>();

function cancelBridgeWait(callId: string): void {
  answeredAt.delete(callId);
  const handles = bridgeWaits.get(callId);
  if (handles === undefined) return;
  clearInterval(handles.interval);
  clearTimeout(handles.ceiling);
  bridgeWaits.delete(callId);
}

/**
 * How often to ask the peer connection whether the caller's audio has arrived.
 *
 * This interval and the confirmation count below are pure ADDED latency: they
 * are how long after the customer is genuinely on the line before we admit it.
 * The first cut polled every 100ms and wanted three confirmations, which put a
 * flat 300ms on top of every call — the very "why does it always take the same
 * beat" this was written to get rid of. getStats is a local read of counters the
 * browser already keeps, so polling it twice as often costs nothing worth
 * measuring.
 */
const BRIDGE_POLL_MS = 50;

/** Absolute backstop. Not a guess at the bridge — a guarantee that a peer which
 *  never reports itself connected cannot leave a live call with no controls. */
const BRIDGE_STRAND_MS = 10_000;
/**
 * Caller legs the calling service has told us are bridged.
 *
 * ── WHY THERE IS NO TIMER HERE ANY MORE ────────────────────────────────────
 *
 * Every version of this before now ended the wait by counting to a number,
 * because the browser cannot see the bridge: its own ICE coming up says nothing
 * about the caller, and no inbound audio is equally what a still-parked leg and
 * a bridged-but-silent caller look like. So the number was a guess at somebody
 * else's service, and it was wrong in both directions — 1.5s made every agent
 * sit through the worst case, and the 250ms that replaced it cleared the bar
 * while the caller was still parked.
 *
 * Measured on Telnyx call_events, 2026-09-23, on a call whose server-side
 * answer-to-bridge window had already been cut to 236ms: the bar cleared at
 * 19:33:05.805 and `call.bridged` landed at 19:33:06.372. The agent had their
 * controls, and spoke their greeting, 567ms before the customer could hear a
 * word of it. That is the failure this whole mechanism exists to prevent,
 * reintroduced by the very constant that was supposed to prevent it.
 *
 * The calling service knows the answer exactly, so it now says so
 * (`call.bridged` → POST /api/voice/events → SSE → {@link useInboxStream}).
 * The wait ends on whichever arrives first:
 *
 *   - the caller's first audio byte, observed locally, which is the fastest
 *     signal there is and needs no round trip;
 *   - this announcement, which is authoritative and arrives even when the
 *     caller is silent or there is no peer connection to watch.
 *
 * Neither is a guess, so neither has a number to tune. What is left is
 * {@link BRIDGE_STRAND_MS}, which is not a latency knob: it is the promise that
 * a call can never be left on a card with no controls.
 *
 * Kept as a set rather than acted on directly because the announcement can in
 * principle beat the wait being armed — the wait arms when the SDK reports the
 * call active, and nothing guarantees that is before the bridge lands. A leg
 * remembered here releases the wait as soon as there is one.
 *
 * BOUNDED, because the stream is tenant-wide: this tab is told about every
 * bridge on the account, including calls answered by somebody else, and those
 * ids match nothing here and would never be cleaned up by a call ending. An id
 * is only worth keeping for the moment between the announcement and the wait
 * arming, so the newest {@link BRIDGED_LEGS_KEPT} are kept and the oldest are
 * dropped. No timer, nothing to tune: a Set iterates in insertion order, so the
 * oldest entry is simply the first one.
 */
const bridgedCallerLegs = new Set<string>();

/** How many announcements to remember. Comfortably more than the calls one tab
 *  can have in flight, and small enough that a tab left open for a week on a
 *  busy tenant holds a few hundred bytes rather than a day of call ids. */
const BRIDGED_LEGS_KEPT = 32;

function rememberBridgedLeg(id: string): void {
  // Re-inserting moves it to the newest position, which is what we want for an
  // id that is still being announced.
  bridgedCallerLegs.delete(id);
  bridgedCallerLegs.add(id);
  while (bridgedCallerLegs.size > BRIDGED_LEGS_KEPT) {
    const oldest = bridgedCallerLegs.values().next().value;
    if (oldest === undefined) break;
    bridgedCallerLegs.delete(oldest);
  }
}

/**
 * Bytes of inbound audio this peer connection has received.
 *
 * `null` means the question could not be ASKED — getStats threw, or the browser
 * does not implement it. It does NOT mean "no audio". An answered call that has
 * not been bridged yet has no `inbound-rtp` audio report at all, because nothing
 * has arrived: that is zero, and zero is the state this whole mechanism exists
 * to wait through.
 *
 * Conflating the two released every call the moment ICE came up, ~1ms after the
 * media path existed and long before the customer was on it — logged in the
 * field as "caller audible after 551ms (cannot observe audio, ICE up at 550ms)".
 * The distinction is the entire difference between watching for the bridge and
 * not watching at all.
 */
async function inboundAudioBytes(pc: RTCPeerConnection): Promise<number | null> {
  try {
    const stats = await pc.getStats();
    let total = 0;
    stats.forEach((report: { type?: string; kind?: string; bytesReceived?: number }) => {
      if (report.type !== 'inbound-rtp' || report.kind !== 'audio') return;
      if (typeof report.bytesReceived !== 'number') return;
      total += report.bytesReceived;
    });
    return total;
  } catch {
    return null;
  }
}

/**
 * The SDK's own breakdown of this call's setup, as one short line.
 *
 * Everything between the agent's click and the caller hearing them splits into
 * a part we can see from the server (the calling service's `call.bridged`,
 * measured per call) and a part only the browser can see. The second part used
 * to be reachable only by subtraction, which is how it ended up being argued
 * about rather than measured.
 *
 * `getEstablishmentTimings()` (SDK 2.27+) reports it directly: when the mic was
 * acquired, when the SDP answer was sent, when ICE connected, and when the
 * secure media channel (DTLS) came up — which is the first moment audio can
 * cross, and so the honest end of the browser's half. Rendered next to the
 * spinner line so one real call from a complaining agent answers "which half is
 * it".
 *
 * Deliberately NOT the first remote track: `ontrack` fires while the remote
 * description is being applied, 400ms before DTLS on the call this was checked
 * against, and reporting it would understate the very window this measures.
 *
 * Optional at every step: older SDK builds have no such method, a test double
 * has no such method, and a call torn down mid-setup has no steps. None of
 * those is worth an exception on the teardown path, so all of them render as
 * nothing at all.
 */
function establishmentSummary(call: TelnyxCall | undefined): string {
  try {
    const timings = call?.getEstablishmentTimings?.();
    const steps = timings?.steps;
    if (!Array.isArray(steps) || steps.length === 0) return '';
    // The few that matter for answer latency, in the order they happen. Any the
    // SDK did not record are simply left out rather than printed as unknown.
    // Measured on a real inbound call 2026-09-25, offsets from the Answer tap:
    // peer 0.1ms, ontrack 15.8ms, mic 16.0ms, SDP sent 18.4ms, ICE connected
    // 194.5ms, DTLS 418.2ms.
    //
    // `First remote audio/video track received` is NOT audio arriving — it is
    // the `ontrack` event, which fires while the remote description is being
    // applied, 400ms before this call could carry a sound. Reporting it as the
    // moment media was up would have understated the window by that whole
    // amount, which is the entire thing this line exists to measure.
    //
    // DTLS is the honest end of the browser's half: no audio crosses before the
    // secure channel exists. ICE stays because the gap between the two was
    // 224ms on that call and is worth seeing separately.
    const wanted: Array<[label: string, name: string]> = [
      ['mic', 'Media devices acquired'],
      ['sdp', 'SDP sent to server'],
      ['ice', 'ICE connection established'],
      ['dtls', 'Secure media channel established (DTLS)'],
    ];
    const parts: string[] = [];
    for (const [label, name] of wanted) {
      const step = steps.find((s) => s?.name === name);
      if (typeof step?.fromStart === 'number') parts.push(`${label} ${Math.round(step.fromStart)}ms`);
    }
    if (parts.length === 0) return '';
    return ` [sdk ${timings?.mode ?? '?'}: ${parts.join(', ')}]`;
  } catch {
    return '';
  }
}

/**
 * Hold the call until the caller is actually on the line.
 *
 * ── Why this watches the audio rather than counting to a number ─────────────
 *
 * This used to be a bare `setTimeout(...)`: the bar reappeared
 * after a fixed wait whether or not the customer was on it. That is wrong in
 * both directions at once — early on a slow bridge, so the greeting is lost
 * anyway, and late on a fast one, so every agent is made to sit through the
 * worst case. It also cannot be tuned right, because the number that is correct
 * depends on a service that deploys separately.
 *
 * So the real thing is measured instead. Until the calling service bridges the
 * customer on, our leg is parked and no audio comes back from it; the moment it
 * lands, the caller's audio starts arriving on this peer connection. Watching
 * `inbound-rtp` bytes climb is that event, observed locally, with no endpoint to
 * add and nothing to keep in sync across two repos.
 *
 * The timer stays, demoted to a CEILING. If the stats are unavailable, or the
 * browser reports them in a shape we do not recognise, or a bridge genuinely
 * never lands, the bar still returns on its own rather than stranding the call
 * on "Connecting…" forever. So this can only ever make the bar appear EARLIER
 * than before, never later.
 *
 * Only ever ends the wait it owns: it does nothing at all if the call ended, or
 * if something else cleared the wait while it ran.
 */
function waitForCallerBridge(set: SetFn, get: () => VoipState, callId: string): void {
  // Belt and braces. The caller only reaches here on the one event that still
  // carries `answering`, so there should never be a second wait to arm — but if
  // that ever stops being true, re-arming on each repeat would walk the deadline
  // forward for as long as the events kept coming, and the bar would never leave
  // "Connecting…". Cheaper to hold the first deadline than to find that out.
  if (bridgeWaits.has(callId)) return;

  const finish = () => {
    cancelBridgeWait(callId);
    const { calls, activeCallId } = get();
    const entry = calls[callId];
    if (!entry?.awaitingBridge) return;
    commit(
      set,
      {
        ...calls,
        [callId]: { ...entry, awaitingBridge: false, startedAt: entry.startedAt ?? Date.now() },
      },
      activeCallId,
      'voip/callerBridged',
    );
  };

  const armedAt = Date.now();
  let polling = false;
  /** When the media path came up. The ceiling is measured from HERE, not from
   *  the answer — see below. */
  let mediaUpAt: number | null = null;

  const done = (why: string) => {
    const at = Date.now();
    // From the click, which is when the agent's spinner started.
    const from = answeredAt.get(callId) ?? armedAt;
    // Left in deliberately. Every question asked about this window so far has
    // been unanswerable without it, and the answers turned out to depend on the
    // agent's distance from Telnyx rather than on anything in this code. One
    // line per answered call is a cheap way for the next person to find that
    // out in seconds instead of a day.
    console.info(
      `[voip] spinner ${at - from}ms from Answer (${why}` +
        (mediaUpAt ? `, media up ${mediaUpAt - from}ms` : ', media never reported up') +
        `, waited ${at - armedAt}ms after Telnyx confirmed)` +
        establishmentSummary(get().calls[callId]?._call),
    );
    answeredAt.delete(callId);
    finish();
  };

  const poll = async () => {
    // Re-entrancy guard: getStats is async and a slow one must not have two
    // samples in flight, which would count the same growth twice.
    if (polling) return;
    polling = true;
    try {
      const entry = get().calls[callId];
      // The call ended, or something else ended the wait. Stop asking.
      if (!entry?.awaitingBridge) return cancelBridgeWait(callId);

      // ── THE ANSWER, FROM THE ONLY PARTY THAT HAS IT ───────────────────────
      //
      // Checked first and before anything to do with the peer connection,
      // because it is the one signal that does not depend on being able to
      // observe the media at all. It is what releases a call whose caller has
      // not spoken, and a call whose `peer` the SDK never attached — both of
      // which used to be handled by counting to a number and hoping.
      const callerLeg = callerLegId(entry);
      const ownLeg = telnyxCallControlId(entry);
      if (
        (callerLeg && bridgedCallerLegs.has(callerLeg)) ||
        (ownLeg && bridgedCallerLegs.has(ownLeg))
      ) {
        return done('the service says bridged');
      }

      const pc = entry._call?.peer?.instance ?? null;
      // No peer, so the bridge cannot be observed from here. That used to
      // release on a cap of its own — an SDK slow to attach `call.peer` handed
      // the agent their controls before the service bridge had landed, losing
      // the greeting this wait exists to protect. Nothing is released on a
      // guess any more: the announcement above covers this case exactly, and
      // the strand backstop is still behind it.
      if (!pc) return;

      // ── THE MEDIA PATH HAS TO EXIST BEFORE ANY OF THIS MEANS ANYTHING ──────
      //
      // The ceiling used to be a plain timer started when the call was answered,
      // and that is wrong wherever ICE is slow. Measured on an agent ~210ms from
      // Telnyx, ICE took 807ms to connect against an 800ms ceiling: the timer
      // fired FIRST, so the bar came back before audio could physically flow.
      // The one thing this mechanism exists to prevent, reintroduced by its own
      // backstop, and only on the connections that can least afford it.
      //
      // Nothing can be heard before the peer connection is up, so the ceiling is
      // measured from the moment it IS up. Until then there is nothing to be
      // late for.
      // Widened on purpose: `connectionState` and `iceConnectionState` have
      // different member sets, and 'completed' belongs only to the latter — a
      // perfectly good "media is up" that the narrower type would reject.
      const state: string = pc.connectionState ?? pc.iceConnectionState;
      if (state !== 'connected' && state !== 'completed') return;
      // ── THE FIRST BYTE FROM THE CALLER IS THE BRIDGE LANDING ──────────────
      //
      // A leg that has been answered but not bridged receives NOTHING: it is
      // parked, and Telnyx sends it no audio at all. Confirmed in the field —
      // at the moment ICE came up the counters read zero ("caller silent"),
      // which is the calling service not having bridged the caller yet.
      //
      // So any inbound audio at all is the signal, and it needs no growth
      // deltas: before the bridge the count is 0, after it is not. That also
      // sidesteps the reason the delta version failed — with DTX a quiet caller
      // may not move the counter between two samples, which read as "still not
      // bridged" and left the spinner running on a live call.
      //
      // Releasing on ICE alone was wrong for the same reason it looked right:
      // ICE being up means WE can be heard, not that anyone is listening.
      if (mediaUpAt === null) mediaUpAt = Date.now();

      const bytes = await inboundAudioBytes(pc);
      if (bytes !== null && bytes > 0) return done('caller audio');

      // Still nothing — either not bridged yet, or bridged to a caller who has
      // not made a sound. Those two were never distinguishable from here, which
      // is why every previous version guessed between them. Now neither has to
      // be: keep watching, and the announcement ends it either way.
      return;
    } finally {
      polling = false;
    }
  };

  const interval = setInterval(() => void poll(), BRIDGE_POLL_MS);
  // A hard stop, far beyond any plausible bridge, so a peer that never reports
  // itself connected cannot strand a live call on a card with no controls.
  const ceiling = setTimeout(() => done('never connected'), BRIDGE_STRAND_MS);
  bridgeWaits.set(callId, { interval, ceiling });
}

/**
 * Give the warmed microphone back once no phone is ringing.
 *
 * The warm stream belongs to the RING, not to a call: it is opened when one
 * starts and is wanted only until that ring is resolved one way or the other.
 * Answered, the call has its own microphone — the warmed one if it claimed it,
 * the SDK's if it did not. Declined, or abandoned by the caller, nobody wants it
 * at all. Either way, holding it past that point would leave the tab's recording
 * indicator lit over an agent who is not on a call.
 *
 * Keyed on "is anything still ringing" rather than on the call that just ended,
 * because a second caller ringing behind the first still needs it warm.
 */
function releaseWarmMicrophoneIfIdle(get: () => VoipState): void {
  if (Object.values(get().calls).some((c) => c.status === 'ringing')) return;
  releaseWarmMicrophone();
}

/**
 * End exactly one call and release only what belongs to it.
 *
 * The mic is the reason this is careful. Ending a call used to stop the store's
 * cached local stream outright, so declining a second, unrelated call killed the
 * capture feeding the call already in progress. Now a local track is stopped
 * only when no surviving call is still sending it — a decision made once and
 * honoured by both the stream cleanup and the peer teardown, since the peer's
 * sender loop would otherwise stop the very track that decision spared.
 */
function endCall(set: SetFn, get: () => VoipState, callId?: string): void {
  const { calls, activeCallId } = get();
  const targetId = callId ?? activeCallId ?? Object.keys(calls)[0];
  if (!targetId) return;
  const entry = calls[targetId];
  if (!entry || tearingDown.has(targetId)) return;

  tearingDown.add(targetId);
  try {
    try {
      // `hangup()` became async in @telnyx/webrtc 2.26.1 and returns a Promise.
      // A synchronous try/catch no longer sees its failure — the rejection would
      // escape as an unhandled one — so the promise is caught in its own right.
      // Still not awaited: teardown must not wait on the socket, and an already
      // torn-down call is exactly the case this ignores.
      void Promise.resolve(entry._call.hangup() as unknown).catch(() => {
        // Already torn down, or the socket is gone — nothing to release.
      });
    } catch {
      // A build whose hangup() still throws synchronously.
    }

    // Neither of this call's ids can be announced again.
    for (const id of [callerLegId(entry), telnyxCallControlId(entry)]) {
      if (id) bridgedCallerLegs.delete(id);
    }

    stopStreamTracks(entry.remoteStream);
    stopStreamTracks(entry._call.remoteStream);

    // Release this call's capture unless a call that survives is actually using
    // the same stream.
    //
    // Deciding on "is anything else still up" is not enough: a ringing call has
    // no capture of its own yet, so an unanswered inbound ring would keep the
    // mic of an outbound attempt open after that attempt was cancelled — and
    // once its entry is gone those tracks are unreachable, which is exactly the
    // leak this teardown exists to prevent. Comparing the streams themselves
    // still protects the other direction: two live calls fed from one device
    // keep it, so ending one can't mute the other.
    const remaining = Object.values(calls).filter((c) => c.id !== targetId);
    const keep = localTracksInUseBy(remaining);
    for (const stream of [entry._localStream, entry._call.localStream]) {
      try {
        stream?.getTracks().forEach((t) => {
          if (!keep.has(t)) t.stop();
        });
      } catch {
        // Stream already gone — nothing to release.
      }
    }
    // Same set, so closing the peer can't undo the decision just made.
    releasePeer(entry._call, keep);

    const next = { ...calls };
    delete next[targetId];
    // Hand focus to whatever is still up, so ending the call you were on does
    // not strand a held caller with no visible UI.
    const nextActive =
      activeCallId === targetId
        ? (Object.values(next).find((c) => c.status !== 'ringing')?.id ?? null)
        : activeCallId;
    commit(set, next, nextActive, 'voip/endCall');
  } finally {
    tearingDown.delete(targetId);
    // A call that ended inside the bridge wait must not be revived by it.
    cancelBridgeWait(targetId);
    // A ring that ended without being answered — declined, or the caller gave
    // up — leaves a microphone open that nothing is going to use.
    releaseWarmMicrophoneIfIdle(get);
  }
}

/** Map a Telnyx `callUpdate` notification onto store state. */
function handleNotification(
  set: SetFn,
  get: () => VoipState,
  notification: TelnyxNotification,
): void {
  if (notification.type !== 'callUpdate') return;
  const call = notification.call;
  if (!call) return;

  const { calls, activeCallId } = get();
  const existing = calls[call.id];

  switch (call.state) {
    case 'ringing':
    case 'requesting': {
      // Outbound ringing is already represented by startCall(); only inbound
      // needs an entry here (Telnyx rings this registered tab directly).
      if (existing || call.direction === 'outbound') break;

      // The initiator's OWN conference leg: they started it, so auto-join it
      // silently — add it straight as an active call (no ringing card) and
      // answer. The people they invited still ring normally. We detect it two
      // ways: the SIP auto-join tag when Telnyx surfaces it, OR — as a fallback
      // for when the SDK drops the custom header — any inbound leg that arrives
      // while the initiator is mid-start (armed conference), so their own leg
      // never rings as an incoming call.
      if (isConferenceAutojoinLeg(call) || isConferenceLegExpected()) {
        if (Object.keys(calls).length >= MAX_CALLS) break;
        // Consumed the armed window — a later real inbound call rings normally.
        useConferenceStore.getState().disarm();
        const joined: CallEntry = {
          id: call.id,
          direction: 'inbound',
          status: 'active',
          incoming: null,
          peerNumber: null,
          remoteStream: null,
          isMuted: false,
          startedAt: Date.now(),
          conference: true,
          _call: call,
          _localStream: null,
        };
        commit(set, { ...calls, [call.id]: joined }, call.id, 'voip/conferenceAutojoin');
        try {
          call.answer();
        } catch {
          // If answering throws, the leg stays as-is; the SDK will retry/hang up.
        }
        break;
      }

      // Dedupe by CALLER NUMBER, not just call.id — but only against a call the
      // new ring could be a DUPLICATE of, never a genuine second call.
      //
      // Deduping on call.id alone let one physical call appear twice: Telnyx can
      // deliver a second leg of the same call under a different id, and an
      // outbound leg's notification does not always carry direction==='outbound'
      // — so a call we placed, or a duplicate ring, slipped past the guards
      // above and was added again as a phantom inbound. Two bars for one call.
      //
      // Both of those duplicates surface while the original is still CONNECTING:
      // a duplicate leg arrives whilst the first is 'ringing', and the outbound
      // echo whilst our outbound call is up. So we only suppress a same-number
      // ring against an outbound call or a still-ringing one. A second call from
      // the same number once the first is ACTIVE/HELD (a call-back, or another
      // line behind that caller ID) is real and must ring through. Matched on the
      // last 10 digits, like the contact cache.
      const callerNumber = call.options?.remoteCallerNumber ?? call.remoteCallerNumber ?? null;
      const callerKey = phoneKey(callerNumber);
      if (
        callerKey &&
        Object.values(calls).some(
          (c) =>
            phoneKey(c.peerNumber) === callerKey &&
            (c.direction === 'outbound' || c.status === 'ringing'),
        )
      ) {
        break;
      }

      if (Object.keys(calls).length >= MAX_CALLS) break;
      const entry: CallEntry = {
        id: call.id,
        direction: 'inbound',
        status: 'ringing',
        incoming: callerInfo(call),
        peerNumber: callerNumber,
        remoteStream: null,
        isMuted: false,
        startedAt: null,
        _call: call,
        _localStream: null,
      };
      // Added alongside whatever is already up — never over the top of it.
      commit(set, { ...calls, [call.id]: entry }, activeCallId, 'voip/incomingCall');
      // Open the microphone NOW, while the phone is ringing and the agent is
      // still reaching for it. The SDK otherwise does it inside `answer()` and
      // holds the SIP 200 behind it — ~190ms of the answer that the caller
      // spends on ringback. See warmMicrophone.
      //
      // Only when the permission is already settled: warming is an optimisation,
      // and an optimisation must never be the thing that puts a permission
      // bubble in front of a ringing call.
      if (get()._micPermission === 'granted') warmMicrophone(get()._micId);
      break;
    }
    // Telnyx reports a connected call under several state names depending on
    // signalling (early media, SIP 200, etc.). Treat them all as "active" —
    // otherwise an answered call whose event is 'answered'/'early'/'established'
    // would leave the HUD stuck on "Calling…". Mirrors the legacy app.
    case 'early':
    case 'answering':
    case 'answered':
    case 'established':
    case 'active': {
      if (!existing) break;
      // `answering` is NOT one of these, whatever the case labels above suggest.
      //
      // The SDK enters State.Answering when it puts the local SDP answer on the
      // wire, before the far end has acknowledged it — its own public mapping
      // reports that state to callers as `"ringing"`, not `"active"`. So the
      // call is not yet up, and the caller can still be hearing ringback.
      //
      // Clearing `answering` here undid the whole point of not stamping the
      // clock in `answerCall`: the bar went back to "In call" with a running
      // timer one state early. It stays in the labels above so the status still
      // moves off 'ringing' — being stuck on "Calling…" is the bug that put
      // this broad set here in the first place — but it no longer counts as
      // confirmation.
      const connected = call.state !== 'answering';
      // ...and our leg being up is still not the caller being able to hear us.
      //
      // An inbound call arrives here as one leg of a forked dial; the calling
      // service bridges it to the caller only once Telnyx tells it we answered,
      // and the caller hears nothing we say until that lands. So a call that we
      // have just answered stays "Connecting…" for that bridge — see
      // waitForCallerBridge, which carries the measurement.
      //
      // Outbound is not the same shape and must not wait: nothing bridges a
      // call this browser dialled, so `call.answered` there IS the other party
      // picking up, and holding the bar would be the lie in reverse.
      const awaitsBridge =
        connected && existing.answering === true && existing.direction === 'inbound';
      // The SDK repeats the connected state, and only the first report carries
      // `answering`. Without the second half, report two would start the clock
      // in the middle of the wait the first one opened.
      const stillWaiting = awaitsBridge || existing.awaitingBridge === true;
      const updated: CallEntry = {
        ...existing,
        // A held call still emits these; leaving it 'held' keeps it parked
        // rather than silently resuming under the caller the user is on.
        status: existing.status === 'held' ? 'held' : 'active',
        remoteStream: call.remoteStream ?? existing.remoteStream,
        _localStream: call.localStream ?? existing._localStream,
        // The clock starts when the call is genuinely up, never before — and
        // for an inbound call that is when the CALLER is on it, not when our
        // own leg answered.
        startedAt: connected && !stillWaiting ? (existing.startedAt ?? Date.now()) : existing.startedAt,
        // Telnyx has confirmed the connection — the call is no longer merely
        // "answered locally", so a later per-call error must not tear it down.
        answering: connected ? false : existing.answering,
        awaitingBridge: stillWaiting || undefined,
      };
      const nextCalls = { ...calls, [call.id]: updated };
      // An outbound call the customer just picked up: the monitor learns who
      // placed it (the server cannot see the user on a browser-dialled call).
      if (existing.direction === 'outbound' && existing.status === 'ringing') {
        reportLiveAgentState(updated);
      }
      // Focus it only if nothing else is; answering explicitly is what moves
      // focus between calls.
      const nextActive = activeCallId && calls[activeCallId] ? activeCallId : call.id;
      commit(set, nextCalls, nextActive, 'voip/callActive');
      if (awaitsBridge) waitForCallerBridge(set, get, call.id);
      break;
    }
    case 'hangup':
    case 'destroy':
    case 'purge': {
      // Only tear down the call this event belongs to. A late event from a call
      // that already ended must not touch the others.
      if (existing) endCall(set, get, call.id);
      break;
    }
    default:
      break;
  }
}

// ── Audio-device recovery ────────────────────────────────────────────────────
// A headset that leaves the OS device list — unplugged, sensor auto-off,
// Bluetooth dropped, laptop slept — ends the mic track the call is sending, and
// nothing re-acquires it: the call stays up on a dead device until the tab is
// reloaded. This walks every live call back onto a device that exists, swapping
// the track underneath rather than dropping the call, so the other party hears
// nothing. `useAudioDeviceRecovery` decides when to run it.

/** The mic track a call is currently sending, from whichever stream still holds
 *  it — the SDK replaces its own `localStream` on a swap, and our mirror of it
 *  is a commit behind. */
export function currentMicTrack(entry: CallEntry): MediaStreamTrack | null {
  for (const stream of [entry._localStream, entry._call.localStream]) {
    try {
      const track = stream?.getAudioTracks()[0];
      if (track) return track;
    } catch {
      // Stream already gone — try the other one.
    }
  }
  return null;
}

/** Calls with media up. A ringing call has no local stream to move yet. */
export const liveCallsOf = (calls: Record<string, CallEntry>): CallEntry[] =>
  Object.values(calls).filter((c) => c.status === 'active' || c.status === 'held');

/**
 * Calls whose microphone a device switch has to move: everything live, plus an
 * outbound call that is still ringing. An outbound call acquires the mic when it
 * is dialled, not when it connects, so leaving it out meant a mic picked during
 * "Calling…" was ignored and the call connected on the old one.
 */
const callsWithMicOf = (calls: Record<string, CallEntry>): CallEntry[] =>
  Object.values(calls).filter(
    (c) =>
      c.status === 'active' ||
      c.status === 'held' ||
      (c.direction === 'outbound' && c.status === 'ringing' && currentMicTrack(c) !== null),
  );

async function runAudioRecovery(set: SetFn, get: () => VoipState): Promise<AudioRecoveryResult> {
  const { _micId, _micLabel, _speakerId, _speakerLabel } = get();
  const [mics, speakers] = await Promise.all([
    enumerateAudioDevices('audioinput'),
    enumerateAudioDevices('audiooutput'),
  ]);

  const mic = resolveDevice(mics, { id: _micId, label: _micLabel });
  const speaker = resolveDevice(speakers, { id: _speakerId, label: _speakerLabel });

  // The device a healthy call should already be on, as a live track would report
  // it. Resolution can land on the `default` pseudo-device either because
  // nothing is pinned or because the pinned headset is unplugged, and in both
  // cases the call is really on whatever `default` stands for — so both go
  // through the same mapping. Comparing against the pseudo-device instead marked
  // every fallen-back call misrouted and re-acquired on every pass, putting a
  // fresh audio gap in a call that was already fine.
  const wanted = concreteDeviceFor(mics, mic.device);

  const live = callsWithMicOf(get().calls);
  const displaced = localTracksInUseBy(live);
  const swapped = new Map<string, MediaStream>();
  let failed = false;

  for (const entry of live) {
    const track = currentMicTrack(entry);
    const onDeviceId = trackDeviceId(track);
    const misrouted = !!wanted && !!onDeviceId && onDeviceId !== wanted.deviceId;
    // Leave a working call alone. `devicechange` fires for every device on the
    // machine — a webcam, a monitor's speakers — and re-acquiring needlessly
    // puts a gap in a call that is perfectly fine.
    if (isTrackUsable(track) && !misrouted) continue;

    const stream = await swapCallMicrophone(entry._call, {
      deviceId: mic.device?.deviceId ?? null,
      muted: entry.isMuted,
    });
    if (!stream) {
      failed = true;
      continue;
    }
    swapped.set(entry.id, stream);
  }

  if (swapped.size) {
    // Re-read the map: a call can end while we are acquiring, and its fresh
    // stream then belongs to nobody.
    const next = { ...get().calls };
    for (const [id, stream] of swapped) {
      const entry = next[id];
      if (entry) next[id] = { ...entry, _localStream: stream };
      else stopStreamTracks(stream);
    }
    commit(set, next, get().activeCallId, 'voip/recoverAudioDevices');

    // Release the captures we replaced — but only those no call is still
    // sending. The same track can be shared by two calls, and stopping one out
    // from under the other mutes a call the user never touched.
    const stillInUse = localTracksInUseBy(callsWithMicOf(get().calls));
    for (const track of displaced) {
      if (stillInUse.has(track)) continue;
      try {
        track.stop();
      } catch {
        // Already stopped — nothing to release.
      }
    }
  }

  set(
    {
      // Re-pin only when we found the device they actually chose, under a new
      // id. Falling back to the system default is temporary: overwriting the
      // preference with it would mean the headset is never picked back up when
      // it returns.
      ...(mic.matched === 'label' && mic.device ? { _micId: mic.device.deviceId } : {}),
      ...(speaker.matched === 'label' && speaker.device
        ? { _speakerId: speaker.device.deviceId }
        : {}),
      _activeSinkId: _speakerId ? (speaker.device?.deviceId ?? null) : null,
      _audioEpoch: get()._audioEpoch + 1,
    },
    false,
    'voip/audioDevicesResolved',
  );

  // A device that came back under a new id has just been re-pinned. Store that
  // id too, so the next reload matches on it directly instead of having to
  // wait for a call to go live before the label match can run.
  if (mic.matched === 'label' || speaker.matched === 'label') rememberAudioDevices(get());

  return { swapped: swapped.size, micLabel: mic.device?.label || null, failed };
}

// One pass at a time: a single unplug arrives as a burst of `devicechange`
// events, and two passes acquiring at once would fight over the same call.
let recoveryInFlight: Promise<AudioRecoveryResult> | null = null;
let recoveryStale = false;

export const useVoipStore = create<VoipState>()(
  devtools(
    (set, get) => ({
      registration: 'unregistered',
      calls: {},
      activeCallId: null,
      callState: 'idle',
      direction: null,
      incoming: null,
      isMuted: false,
      isHeld: false,
      remoteStream: null,
      _client: null,
      _callerNumber: null,
      _callerNumbers: [],
      _peerNumber: null,
      ...rememberedAudioDevices(),
      _audioEpoch: 0,
      _peerBusy: false,
      _callWaitingTone: readCallWaitingTone(),
      _micPermission: 'unknown',

      register: async () => {
        // No calling service configured for this build (VITE_CALLING_SERVICE_URL
        // unset) — skip registration so we never POST to a dead same-origin
        // /api/devices/sip/credentials on every page load. The dialer stays inert.
        if (!isCallingConfigured) {
          if (get().registration !== 'unregistered') {
            set({ registration: 'unregistered' }, false, 'voip/unconfigured');
          }
          return;
        }
        const { registration, _client } = get();
        if (registration === 'registering' || _client) return;
        set({ registration: 'registering' }, false, 'voip/registering');
        // Captured before any await. See registrationEpoch.
        const epoch = registrationEpoch;
        // Settle the microphone now, while the phone is coming up and nobody is
        // on the line. The SDK asks for it inside `call.answer()` and holds the
        // SIP 200 OK until the agent responds to the bubble, so left until then
        // the first call of the day is answered in the UI and still ringing for
        // the caller — for as long as it takes to notice the prompt. Not
        // awaited: registration must not wait on a dialog, and a browser that
        // refuses to ask without a user gesture (Safari) has to register anyway.
        void ensureMicrophoneAccess().then((permission) => {
          set({ _micPermission: permission }, false, 'voip/micPermission');
          // Resolve the remembered devices against the ones that exist right
          // now, before any call arrives. Recovery is otherwise driven only by
          // a device CHANGING, so a remembered id that went stale between
          // sessions — Chrome regenerates them — would not be re-matched by
          // label until something else moved, and the first call of the day
          // would come out of the laptop speaker after all. Doing it here and
          // not at store creation is deliberate: enumerating before the
          // permission is settled returns blank labels, and the label is the
          // whole point. Only when there is something to resolve.
          // Only once the microphone is actually granted. Without it
          // enumerateDevices returns blank labels, nothing matches, and the
          // pass below writes `_activeSinkId: null` — discarding the remembered
          // headset and sending the call out of the laptop speaker, which is
          // the complaint this whole path exists to fix. Left alone, the stored
          // preference survives for the next pass to resolve.
          const { _micId, _speakerId } = get();
          if (permission === 'granted' && (_micId || _speakerId)) {
            void get().recoverAudioDevices();
          }
        });
        try {
          // Resolve the tenant's outbound caller-ID alongside the SIP creds so
          // the first outbound call already has a number to place from. Non-fatal
          // — inbound still works without it; only outbound needs a callerNumber.
          const [creds, callerNumbers] = await Promise.all([
            fetchSipCredentials(),
            fetchCallerNumbers(),
          ]);
          // Checked BEFORE anything is written, not just before the client is
          // built. `_callerNumber` is global store state, not something the
          // stale attempt owns: if tenant A is unregistering while B registers
          // and A's fetch resolves last, A would stamp its outbound caller IDs
          // over B's and only then bail at the client guard — leaving B's SIP
          // session presenting A's numbers, which shows the wrong line or gets
          // the call rejected outright. There is a second check after
          // resolveTelnyxRTC() because that await can also outlive an
          // unregister.
          if (epoch !== registrationEpoch) return;
          // The first line is the default caller ID, as it has always been;
          // the rest are only consulted when a caller asks to present one of
          // them (calling a customer back on the line they rang).
          set(
            { _callerNumber: callerNumbers[0] ?? null, _callerNumbers: callerNumbers },
            false,
            'voip/callerNumber',
          );
          const region = telnyxRegion();
          if (region) console.info(`[voip] registering against the ${region} edge`);
          const TelnyxRTC = await resolveTelnyxRTC();
          // Everything above was async. If an unregister happened while the
          // credentials were in flight, this attempt is stale: registering it
          // would put a second SIP session on the same credential and leave
          // nothing holding the handle to shut it down.
          //
          // Checked BEFORE constructing rather than after. `new TelnyxRTC()`
          // logs its version banner from the constructor, so building one only
          // to throw it away left two "SDK version" lines in the console of
          // every page load — harmless, but indistinguishable at a glance from
          // the double registration this guard exists to prevent.
          if (epoch !== registrationEpoch) return;
          const client = new TelnyxRTC({
            login: creds.sipId,
            password: creds.sipSecret,
            ...(creds.realm ? { realm: creds.realm } : {}),
            ...(creds.wsServers ? { wsServers: creds.wsServers } : {}),
            // Match the legacy app's ICE setup so calls negotiate reliably.
            //
            // `trickleIce` is load-bearing, not decoration. With it OFF — the
            // SDK's own default — `_onIce` holds the SDP answer back until ICE
            // gathering COMPLETES or a 1000ms timer fires, so the SIP 200 (and
            // with it the moment Telnyx can bridge the caller to us) would queue
            // behind TURN allocation on every single call. With it on, the
            // answer goes out straight after `createAnswer` and candidates
            // follow one at a time. Do not remove it.
            trickleIce: true,
            prefetchIceCandidates: true,
            ...(region ? { region } : {}),
            // Ask Telnyx for the SDP answer early — sent as
            // `loginParams.early_sdp_answer`, and described by them as "faster
            // media negotiation on supported backends". A backend that does not
            // support it ignores the login param.
            earlySdpAnswer: true,
            // NEVER stop trying to get back on. The SDK's default is ten
            // attempts and then `_autoReconnect = false` for the life of the
            // client — after which this tab is not ringable again until someone
            // reloads the page, with nothing on screen saying so. Ten attempts
            // is minutes; an agent leaves this tab open all day, and a laptop
            // that sleeps through lunch or a Wi-Fi that moves will outlast it.
            // 0 means unlimited; the SDK's own backoff is 1s doubling to a 30s
            // ceiling with jitter, so "forever" is about two attempts a minute.
            maxReconnectAttempts: 0,
            // Keep a call up when the socket dies under it. On a PUNT, or while
            // reconnecting, the SDK otherwise purges live calls; with this it
            // drops the socket and leaves the media alone, and the call carries
            // on while signalling comes back.
            //
            // It changes nothing when no call is up: the reconnect path checks
            // for a peer whose signalling is still open and, finding none,
            // logs "all peer connections have signalingState closed, doing full
            // reconnect" and takes exactly the path it takes today. So the idle
            // tab — which is most of the day — is untouched.
            keepConnectionAliveOnSocketClose: true,
          });
          // ── EVERY CALLBACK BELOW BELONGS TO *THIS* CLIENT, NOT TO THE STORE ──
          //
          // `unregister()` disconnects the client and moves the epoch on, but a
          // disconnect does not un-queue an event the SDK has already decided to
          // emit. Without this gate a `telnyx.ready` in flight when the agent
          // logs out or switches org lands afterwards and sets
          // `registration: 'registered'` while `_client` is null — a tab that
          // reads as registered and can neither ring nor dial. The same applies
          // to the old client's notifications and errors, which would otherwise
          // mutate the NEXT tenant's calls.
          //
          // The synchronous paths in this action already check `epoch` for
          // exactly this reason; these three were the ones left open, and the
          // retry above makes clients churn far more often than before.
          const isCurrent = () => epoch === registrationEpoch;

          client.on('telnyx.ready', () => {
            if (!isCurrent()) return;
            // Up. Any revival waiting on the old attempt is now answering a
            // question that has resolved itself.
            cancelRegistrationRevival();
            set({ registration: 'registered' }, false, 'voip/ready');
          });
          client.on('telnyx.notification', (n) => {
            if (!isCurrent()) return;
            handleNotification(set, get, n);
          });
          client.on('telnyx.error', (n) => {
            if (!isCurrent()) return;
            // Only a failure to establish the INITIAL connection marks the phone
            // unavailable. A per-call error (e.g. a rejected INVITE, or the
            // "CALL DOES NOT EXIST" that fires as a failed call tears down) must
            // NOT unregister the softphone — otherwise the next call is blocked.
            if (get().registration === 'registering') {
              set({ registration: 'failed' }, false, 'voip/registerError');
              scheduleRegistrationRevival(get);
              return;
            }
            // That per-call error is still terminal for the call it names, and
            // the SDK sends no callUpdate after it — so without this the call
            // never clears. Limited to a call Telnyx never confirmed: still
            // ringing, or answered here but not yet connected (`answering`).
            // An error on a call that IS up can be transient, and dropping a
            // call the user is talking on would be far worse than ignoring it.
            const erroredId = n?.call?.id;
            const errored = erroredId ? get().calls[erroredId] : undefined;
            if (errored && (errored.status === 'ringing' || errored.answering)) {
              endCall(set, get, errored.id);
            }
          });
          client.connect();
          // Stay 'registering' until the SDK confirms readiness (telnyx.ready) —
          // connect() only starts the async handshake, so flipping to
          // 'registered' here would enable the dialer before a call can place.
          set({ _client: client }, false, 'voip/connecting');
        } catch {
          // Same gate as the success path, for the same reason. A stale
          // attempt's credential fetch rejecting says nothing about the
          // registration that replaced it: without this, tenant A failing after
          // an unregister marks tenant B's phone as failed while B's client is
          // connected and perfectly able to take calls.
          if (epoch !== registrationEpoch) return;
          set({ registration: 'failed' }, false, 'voip/registerFailed');
          scheduleRegistrationRevival(get);
        }
      },

      unregister: () => {
        // Invalidate any registration still in flight before tearing down the
        // one that exists — otherwise its client arrives moments later and
        // re-registers a session nobody asked for.
        registrationEpoch += 1;
        // Taking the phone down on purpose (logout, org switch, unmount) must
        // not be undone a few seconds later by a retry that was already armed.
        cancelRegistrationRevival();
        // The phone is going offline; it has no business holding the device.
        releaseWarmMicrophone();
        const { _client } = get();
        // Tear every call down, not just the focused one.
        Object.keys(get().calls).forEach((id) => endCall(set, get, id));
        try {
          _client?.disconnect();
        } catch {
          // Ignore — may already be disconnected.
        }
        set({ _client: null, registration: 'unregistered' }, false, 'voip/unregister');
      },

      applyCallBridged: ({ callControlId, agentLeg }) => {
        // BOTH ids are remembered, because neither is guaranteed to be one this
        // browser can recognise.
        //
        // The caller's leg reaches an inbound call through `X-Caller-Leg` on the
        // INVITE — and the calling service attaches that header only when every
        // destination in the dial is a SIP device, since a custom header on a
        // PSTN leg is unreadable at best and a rejected dial at worst. So a ring
        // group that also dials a forwarding number leaves the web client with
        // no caller leg at all, and `client_state` does not reach an INVITE, so
        // there is no second source for it.
        //
        // `agentLeg` is the leg the bridge webhook named, which may be the
        // registered-client leg this browser is actually on — in which case
        // {@link telnyxCallControlId} matches it. It may equally be the outer
        // leg the service dialled, which this browser never sees; an id that
        // matches nothing simply ages out. Remembering both costs one set entry
        // and is the difference between releasing the bar and waiting out the
        // strand on a mixed ring group.
        //
        // Remembered rather than acted on: the wait may not be armed yet, and
        // the poll picks this up within one interval either way.
        if (callControlId) rememberBridgedLeg(callControlId);
        if (agentLeg) rememberBridgedLeg(agentLeg);
      },

      applyCallIncoming: (payload) => {
        const from = payload.from ?? '';
        const contactId = payload.contactId ?? null;
        const contactName = payload.contactName ?? null;
        if (!contactId && !contactName) return;

        // Cache first, so a Telnyx ring that arrives AFTER this event still
        // enriches (callerInfo reads the cache).
        cacheContact(from, contactId, contactName);

        // Enrich whichever ringing call this event belongs to, matched by number.
        const { calls, activeCallId } = get();
        const match = Object.values(calls).find(
          (c) =>
            c.incoming && phoneKey(c.incoming.from) && phoneKey(c.incoming.from) === phoneKey(from),
        );
        if (!match?.incoming) return;
        const updated: CallEntry = {
          ...match,
          incoming: { ...match.incoming, contactId, contactName },
        };
        commit(set, { ...calls, [match.id]: updated }, activeCallId, 'voip/enrichIncoming');
      },

      startCall: (destinationNumber, options) => {
        const {
          _client,
          callState,
          _callerNumber,
          _callerNumbers,
          _micId,
          _micLabel,
          _speakerId,
          calls,
          activeCallId,
        } = get();
        if (!_client || callState !== 'idle') return;
        // A caller ID is mandatory: Telnyx rejects an outbound PSTN leg with no
        // callerNumber ("CALL DOES NOT EXIST"), stranding the UI in outbound
        // ringing until setup fails. Refuse here so every caller (dialer AND
        // contactComms) is protected, not just the ones that remember to check.
        if (!_callerNumber) {
          console.warn('[voip] startCall refused: no caller ID available for this tenant');
          return;
        }
        // Present the line the caller asked for when it is still one of ours,
        // matched on the last ten digits so the stored spelling wins whatever
        // the call log carried. A line we no longer own — ported away, since
        // deactivated — falls back to the default rather than failing the call:
        // Telnyx rejects a callerNumber the account cannot present.
        const requested = phoneKey(options?.fromNumber);
        const callerNumber =
          (requested ? _callerNumbers.find((line) => phoneKey(line) === requested) : null) ??
          _callerNumber;
        const preferredCodecs = getPreferredAudioCodecs();
        const clientRef = mintClientRef();
        const call = _client.newCall({
          destinationNumber,
          // Telnyx needs a caller-ID to place the outbound PSTN leg — without it
          // the call dies at setup ("CALL DOES NOT EXIST"). Same as the legacy app.
          callerNumber,
          callerName: '',
          audio: CALL_AUDIO_CONSTRAINTS,
          video: false,
          // Hand the SDK the id AND the label rather than pinning
          // `deviceId: { exact }` ourselves. It resolves the id against the
          // devices that exist right now, falls back to matching the label, and
          // finally drops the constraint for the system default. Our own exact
          // id skipped all of that, so once it went stale — which is what
          // unplugging a headset does — the call failed outright with an
          // OverconstrainedError and no microphone at all.
          ...(_micId ? { micId: _micId, micLabel: _micLabel ?? '' } : {}),
          ...(_speakerId ? { speakerId: _speakerId } : {}),
          ...(preferredCodecs ? { preferred_codecs: preferredCodecs } : {}),
          // Names THIS call to the calling service, which records the call the
          // reference turned out to be (the SDK never tells us an outbound
          // call's id). Presented later as proof the call is this browser's.
          customHeaders: [{ name: CLIENT_REF_HEADER, value: clientRef }],
        });
        const entry: CallEntry = {
          id: call.id,
          direction: 'outbound',
          status: 'ringing',
          incoming: null,
          peerNumber: destinationNumber.trim(),
          remoteStream: null,
          isMuted: false,
          startedAt: null,
          clientRef,
          _call: call,
          _localStream: null,
        };
        commit(set, { ...calls, [call.id]: entry }, activeCallId, 'voip/startCall');
      },

      joinConferenceRoom: (dialTarget, callerNumber) => {
        const blocked = conferenceJoinBlocker(get());
        if (blocked) return blocked;
        const { _client, _callerNumber, _micId, _micLabel, _speakerId, calls } = get();
        // Already covered by the blocker; this narrows the type for newCall.
        if (!_client) return "Your phone line isn't connected yet. Try again in a moment.";
        // The room only admits a leg presenting the company number it was
        // created with, so the service's own caller ID wins when it sends one.
        const from = callerNumber || _callerNumber;
        const preferredCodecs = getPreferredAudioCodecs();
        const call = _client.newCall({
          destinationNumber: dialTarget,
          ...(from ? { callerNumber: from } : {}),
          callerName: '',
          audio: CALL_AUDIO_CONSTRAINTS,
          video: false,
          ...(_micId ? { micId: _micId, micLabel: _micLabel ?? '' } : {}),
          ...(_speakerId ? { speakerId: _speakerId } : {}),
          ...(preferredCodecs ? { preferred_codecs: preferredCodecs } : {}),
        });
        const entry: CallEntry = {
          id: call.id,
          direction: 'outbound',
          status: 'ringing',
          incoming: null,
          peerNumber: null,
          remoteStream: null,
          isMuted: false,
          startedAt: null,
          conference: true,
          _call: call,
          _localStream: null,
        };
        commit(set, { ...calls, [call.id]: entry }, call.id, 'voip/joinConferenceRoom');
        return null;
      },

      hangupConferenceLegs: () => {
        for (const entry of Object.values(get().calls)) {
          if (entry.conference) endCall(set, get, entry.id);
        }
      },

      answerCall: (rawCallId) => {
        const callId = asCallId(rawCallId);
        const { calls, activeCallId } = get();
        const target = callId
          ? calls[callId]
          : Object.values(calls).find((c) => c.status === 'ringing');
        if (!target) return;

        // Answer FIRST, before parking anything. The caller can hang up in the
        // moment between the ring and the tap, and the SDK then rejects the
        // answer outright — there is no call left to connect to. Marking it
        // 'active' regardless (what this used to do) stranded a phantom call in
        // the HUD: a running timer, no audio, and no further SDK notification
        // coming for a call that is already destroyed, so only hitting Hang up
        // cleared it. Treat a failed answer as what it is — the call is gone.
        //
        // Doing this before the hold below also means a failed answer leaves the
        // call already in progress exactly as it was, rather than parking it for
        // a call that never connected.
        //
        // Hand it the microphone chosen NOW. The SDK copies the mic into an
        // inbound call's options when the INVITE arrives and acquires from that
        // copy on answer, so a device picked while the call was ringing used to
        // be ignored — the call connected on the old mic. No local stream exists
        // before answer, so nothing needs swapping; this just points the capture
        // at the right device. Cleared rather than left alone when following the
        // system default, or a stale pin would win.
        //
        // And hand it the microphone that was opened while the phone rang, if
        // there is one. `_retrieveLocalStream` returns `options.localStream`
        // verbatim when it is a MediaStream, so this is the whole of the saving:
        // no `getUserMedia`, no device enumeration, straight to the peer
        // connection. A null claim — nothing warmed, or the agent picked a
        // different mic mid-ring — leaves the micId/micLabel above to do exactly
        // what they did before.
        const { _micId, _micLabel } = get();
        if (target.status === 'ringing' && target._call.options) {
          target._call.options.micId = _micId ?? undefined;
          target._call.options.micLabel = _micId ? (_micLabel ?? '') : undefined;
          const warmed = claimWarmMicrophone(_micId);
          if (warmed) target._call.options.localStream = warmed;
        }
        try {
          target._call.answer();
        } catch {
          endCall(set, get, target.id);
          return;
        }

        const next = { ...calls };
        // Park the call in progress rather than dropping it. This is the whole
        // fix for "answering the second call kills the first".
        const current = activeCallId ? next[activeCallId] : null;
        if (current && current.id !== target.id && current.status === 'active') {
          try {
            current._call.hold?.();
          } catch {
            // Hold unsupported on this build — the call stays up regardless.
          }
          next[current.id] = { ...current, status: 'held' };
          reportLiveAgentState(current, { onHold: true });
        }

        next[target.id] = {
          ...target,
          status: 'active',
          // NOT started yet. `answer()` only begins the answer: the SDK has to
          // acquire the microphone first, and if the agent has never granted it
          // the browser puts a permission bubble up and holds the SIP 200 OK
          // behind it. Stamping the clock here started a timer — and a bar
          // reading "In call" — over a caller who was still hearing ringback,
          // and left a call that was never connected looking connected when the
          // agent clicked Block. The clock starts where `answering` is cleared:
          // when Telnyx says the call is really up.
          startedAt: target.startedAt,
          // Answered here, not yet confirmed by Telnyx. See CallEntry.answering:
          // this is what still lets a late failure tear the call down, since it
          // no longer reads as ringing.
          answering: true,
        };
        answeredAt.set(target.id, Date.now());
        commit(set, next, target.id, 'voip/answerCall');
        // The answered call owns its microphone now (claimed above, or acquired
        // by the SDK). Anything still warm belongs to a ring that is over.
        releaseWarmMicrophoneIfIdle(get);
      },

      declineCall: (rawCallId) => {
        const callId = asCallId(rawCallId);
        const { calls } = get();
        const target = callId
          ? calls[callId]
          : Object.values(calls).find((c) => c.status === 'ringing');
        if (!target) return;
        // Declining a ringing call must never disturb a call already up.
        endCall(set, get, target.id);
      },

      hangup: (rawCallId) => {
        endCall(set, get, asCallId(rawCallId));
      },

      swapTo: (rawCallId) => {
        const callId = asCallId(rawCallId);
        const { calls, activeCallId } = get();
        const target = callId ? calls[callId] : undefined;
        if (!target || target.status === 'ringing') return;
        const next = { ...calls };
        const current = activeCallId ? next[activeCallId] : null;
        if (current && current.id !== callId && current.status === 'active') {
          try {
            current._call.hold?.();
          } catch {
            /* keep the call up even if hold is unavailable */
          }
          next[current.id] = { ...current, status: 'held' };
          reportLiveAgentState(current, { onHold: true });
        }
        try {
          target._call.unhold?.();
        } catch {
          /* fall through — status still reflects the user's intent */
        }
        if (target.status === 'held') reportLiveAgentState(target, { onHold: false });
        next[target.id] = { ...target, status: 'active' };
        commit(set, next, target.id, 'voip/swapTo');
      },

      muteToggle: (rawCallId) => {
        const { calls, activeCallId } = get();
        const id = asCallId(rawCallId) ?? activeCallId;
        const entry = id ? calls[id] : null;
        if (!entry) return;
        const next = !entry.isMuted;
        // Prefer the Telnyx call API (signals the peer); fall back to the local
        // track if the SDK build doesn't expose mute helpers.
        if (next && entry._call.muteAudio) entry._call.muteAudio();
        else if (!next && entry._call.unmuteAudio) entry._call.unmuteAudio();
        else {
          const track = entry._localStream?.getAudioTracks()[0];
          if (track) track.enabled = !next;
        }
        commit(
          set,
          { ...calls, [entry.id]: { ...entry, isMuted: next } },
          activeCallId,
          'voip/muteToggle',
        );
      },

      holdToggle: (rawCallId) => {
        const { calls, activeCallId } = get();
        const id = asCallId(rawCallId) ?? activeCallId;
        const entry = id ? calls[id] : null;
        if (!entry || entry.status === 'ringing') return;
        const hold = entry.status !== 'held';
        if (hold) entry._call.hold?.();
        else entry._call.unhold?.();
        reportLiveAgentState(entry, { onHold: hold });
        commit(
          set,
          { ...calls, [entry.id]: { ...entry, status: hold ? 'held' : 'active' } },
          activeCallId,
          'voip/holdToggle',
        );
      },

      sendDtmf: (digit, rawCallId) => {
        const { calls, activeCallId } = get();
        const id = asCallId(rawCallId) ?? activeCallId;
        if (id) calls[id]?._call.dtmf?.(digit);
      },
      setCallerNumber: (n) => {
        set({ _callerNumber: n }, false, 'voip/setCallerNumber');
      },
      setCallWaitingTone: (enabled) => {
        set({ _callWaitingTone: enabled }, false, 'voip/setCallWaitingTone');
        try {
          localStorage.setItem(CALL_WAITING_TONE_KEY, enabled ? 'on' : 'off');
        } catch {
          /* storage blocked — the choice still holds for this session */
        }
      },
      setMicId: (id, label) => {
        set({ _micId: id, _micLabel: id ? (label ?? null) : null }, false, 'voip/setMicId');
        // Apply live so an in-call device switch takes effect immediately.
        const client = get()._client as {
          setAudioSettings?: (s: { micId?: string; micLabel?: string }) => void;
          micId?: string;
          micLabel?: string;
        } | null;
        try {
          if (id) {
            // The label travels with the id: the SDK matches one against the
            // other when it builds the next call's constraints, which is what
            // keeps the choice working after the device re-enumerates.
            client?.setAudioSettings?.({ micId: id, micLabel: label ?? '' });
            if (client) {
              client.micId = id;
              client.micLabel = label ?? '';
            }
          }
        } catch {
          /* client may not support live device switching — used on next call */
        }
        rememberAudioDevices(get());
        // Move any call already up onto it, rather than waiting for the next one.
        void get().recoverAudioDevices();
      },
      setSpeakerId: (id, label) => {
        set(
          { _speakerId: id, _speakerLabel: id ? (label ?? null) : null, _activeSinkId: id },
          false,
          'voip/setSpeakerId',
        );
        const client = get()._client as { speakerId?: string } | null;
        try {
          if (client && id) client.speakerId = id;
        } catch {
          /* applied on next call */
        }
        rememberAudioDevices(get());
      },
      refreshMicPermission: async () => {
        // Never prompts. The agent has been told the microphone is blocked and
        // has gone to the browser to change it; asking again from here would
        // only be refused by a browser that has already been told no.
        const permission = await readMicrophonePermission();
        // 'unknown' is "this browser will not say" — Firefox and older Safari
        // expose no `microphone` descriptor at all. It is not news, and writing
        // it over a state we established the hard way (a getUserMedia that came
        // back NotAllowedError) would erase the blocked-microphone warning from
        // both the panel and the HUD. On those browsers the panel the agent was
        // told to open would be the thing that hid the diagnosis.
        if (permission === 'unknown') return get()._micPermission;
        set({ _micPermission: permission }, false, 'voip/micPermissionRefreshed');
        return permission;
      },

      recoverAudioDevices: () => {
        if (recoveryInFlight) {
          // The device list moved again while we were acquiring, and the pass in
          // flight may have read it before that — send it round once more rather
          // than dropping the event.
          recoveryStale = true;
          return recoveryInFlight;
        }
        recoveryStale = false;
        const pass = (async () => {
          let result = await runAudioRecovery(set, get);
          while (recoveryStale) {
            recoveryStale = false;
            result = await runAudioRecovery(set, get);
          }
          return result;
        })();
        recoveryInFlight = pass;
        return pass.finally(() => {
          recoveryInFlight = null;
        });
      },
    }),
    { name: 'VoipStore', enabled: import.meta.env.DEV },
  ),
);

// A lapsed session waits for this to go false before it signs the user out —
// see `redirectToLogin`. Subscribed here rather than set inside `commit` so
// no write path to `calls` can be missed.
useVoipStore.subscribe((s) => setCallsInFlight(Object.keys(s.calls).length > 0));

// ── Typed selector hooks ────────────────────────────────────────────────────
export const useRegistrationState = () => useVoipStore((s) => s.registration);
export const useCallState = () => useVoipStore((s) => s.callState);
export const useCallDirection = () => useVoipStore((s) => s.direction);
export const useActiveCallId = () => useVoipStore((s) => s.activeCallId);
export const useIncomingCall = () => useVoipStore((s) => s.incoming);
export const useIsMuted = () => useVoipStore((s) => s.isMuted);
export const useIsHeld = () => useVoipStore((s) => s.isHeld);
export const useRemoteStream = () => useVoipStore((s) => s.remoteStream);
export const useCallerNumber = () => useVoipStore((s) => s._callerNumber);
export const usePeerNumber = () => useVoipStore((s) => s._peerNumber);
export const useMicId = () => useVoipStore((s) => s._micId);
export const useSpeakerId = () => useVoipStore((s) => s._speakerId);
/** The output id the `<audio>` sinks should be on, plus the counter that tells
 *  them to re-apply it after audio has been re-routed. */
export const useActiveSinkId = () => useVoipStore((s) => s._activeSinkId);
export const useAudioEpoch = () => useVoipStore((s) => s._audioEpoch);
export const useCallWaitingTone = () => useVoipStore((s) => s._callWaitingTone);
/** Whether this browser will give the softphone a microphone at all. */
export const useMicPermission = () => useVoipStore((s) => s._micPermission);
/** The focused call, for anything that needs more than the flat mirrors. */
export const useActiveCall = () =>
  useVoipStore((s) => (s.activeCallId ? (s.calls[s.activeCallId] ?? null) : null));
/**
 * One specific call, by id, whether or not it is the focused one.
 *
 * For screens that belong to a PARTICULAR call rather than to whichever is
 * active now: a job form opened from call A must keep showing A's transcript
 * and record A's id even after the agent answers B. Reading `useActiveCall()`
 * there silently re-points the page at the new conversation mid-booking.
 *
 * Returns the live entry, so callers still see the mutable SDK fields as they
 * populate — and null once the call has ended and left the store.
 */
export const useCallById = (callId: string | null | undefined) =>
  useVoipStore((s) => (callId ? (s.calls[callId] ?? null) : null));
/**
 * The call with this Telnyx call-control id, if it is still up.
 *
 * The id is how the rest of the system names a call, so a page handed one —
 * "make a job out of THIS call" — has to be able to find the entry it refers
 * to. Falling back to the focused call instead is how a form ends up pinned to
 * whoever the agent happens to be talking to now rather than the caller it was
 * opened for.
 *
 * Returns the entry itself, so the reference stays stable between renders while
 * the store is unchanged.
 */
export const useCallByControlId = (controlId: string | null | undefined) =>
  useVoipStore((s) =>
    controlId
      ? (Object.values(s.calls).find((call) => telnyxCallControlId(call) === controlId) ?? null)
      : null,
  );
/** Calls ringing right now — one bar each, on top of whatever is connected. */
export const useRingingCalls = () => useVoipStore(useShallow((s) => ringingCallsOf(s.calls)));
/** Calls that are up but not focused (typically parked by answering another). */
export const useBackgroundCalls = () =>
  useVoipStore(useShallow((s) => backgroundCallsOf(s.calls, s.activeCallId)));
/** Every remote stream that should be audible/attached, keyed by call. */
/** Calls that have a remote stream to play — the CallEntry objects themselves,
 *  so `useShallow` compares stable references. Mapping to fresh `{stream,…}`
 *  objects here would fail every shallow-equality check and re-render forever
 *  (RemoteAudio: "Maximum update depth exceeded"). The component reads the
 *  fields off the entry instead. */
export const useCallsWithStream = () =>
  useVoipStore(useShallow((s) => Object.values(s.calls).filter((c) => c.remoteStream)));
export const useVoipActions = () =>
  useVoipStore(
    useShallow((s) => ({
      register: s.register,
      unregister: s.unregister,
      startCall: s.startCall,
      answerCall: s.answerCall,
      declineCall: s.declineCall,
      hangup: s.hangup,
      swapTo: s.swapTo,
      muteToggle: s.muteToggle,
      holdToggle: s.holdToggle,
      sendDtmf: s.sendDtmf,
      setCallerNumber: s.setCallerNumber,
      setCallWaitingTone: s.setCallWaitingTone,
      refreshMicPermission: s.refreshMicPermission,
      setMicId: s.setMicId,
      setSpeakerId: s.setSpeakerId,
    })),
  );

/** Internals exposed for tests only. */
export const __testing = {
  regionFromTimeZone,
  telnyxRegion,
  establishmentSummary,
  cancelRegistrationRevival,
};
